import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useOutletContext, useParams, useLocation, useSearchParams } from "react-router-dom";
import { FeatureGate } from "@/components/shared/FeatureGate";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
  ComboboxCollection,
} from "@/components/ui/combobox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useCreateExperiment,
  useExperimentPages,
  useAbTestDraft,
} from "@/hooks/useExperiments";
import { useAnalyticsData } from "@/hooks/useAnalytics";
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { usePlanUsage, useInvalidatePlanUsage } from "@/hooks/usePlanUsage";
import { PlanLimitBanner, CreateOrUpgradeButton, buildPlanLimitCopy } from "@/components/shared/plan-usage";
import { getUserFacingApiError, isPlanLimitApiError, trackSplitTestSetup } from "@/lib/api/api";
import { getPlanDisplayName } from "@/lib/constants/pricing";
import { track } from "@/lib/utils/track";
import {
  logExperimentEvent,
  reportExperimentFailure,
} from "@/lib/observability/experiments";
import { cn } from "@/lib/utils";
import { destinationCountsLogins } from "@/lib/experiments/outcomeLadder";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { TestPlanningModal } from "@/components/experiments/TestPlanningModal";
import {
  defaultExperimentGoal,
  EXPERIMENT_GOAL_LABELS,
  getExperimentSetupHint,
  getOnboardingGoalLabel,
  getPlatformLabel,
  getPlatformVariantTip,
  preferredTestType,
  suggestPageForExperiment,
  suggestedExperimentGoals,
  type ExperimentGoalId,
} from "@/lib/config/experimentContext";
import { resolveSiteContext, setSiteContextPlatform } from "@/lib/utils/siteContext";
import type { PlatformId } from "@/lib/types/platform";
import { normalizePlatformId } from "@/lib/utils/detectPlatform";
import { isOnboardingGoalId } from "@/lib/types/onboarding-goal";
import { useDetectSitePlatform } from "@/hooks/useDetectSitePlatform";
import { PlatformGlyph } from "@/components/onboarding/PlatformGlyph";
import { ExperimentPlatformStrip } from "@/components/experiments/ExperimentPlatformStrip";
import { ExperimentPlanPanel } from "@/components/experiments/ExperimentPlanPanel";
import { QAPreview, isQAReady } from "@/components/experiments/QAPreview";
import { NewTestPagePicker } from "@/components/experiments/NewTestPagePicker";
import { toSiteBase } from "@/components/experiments/ComponentEditor/iframeUrl";
import { EmptyState } from "@/components/shared/layout";
import { EXPERIMENT_GOALS } from "@/components/experiments/experimentGoals";
import { DRAFT_STORAGE_KEY, generateDraftId, newElementDraft, writeSplitTestDraft, type DraftSplitTestState } from "@/components/experiments/splitTestDraft";
import { PAGE_TRAFFIC_DAYS, buildPickerPages, formatAbout, pageTitle, parseTestDeepLink, planForPicker, planSummaryLine, type PlannerState } from "@/components/experiments/newTestModel";
import { ShellHeaderActions } from "@/components/layout/ShellHeader";
import { getPagesBreakdown } from "@/lib/api/external-api";
import { useQuery } from "@tanstack/react-query";
import {
  TrafficDistributionPanel,
  equalTrafficDistribution,
  normalizeTrafficDistribution,
} from "@/components/experiments/TrafficDistributionPanel";
import { SectionEyebrow } from "@/components/shared/layout";
import { countryCodes } from "@/lib/constants/countries";
import {
  RESTRICTION_DEVICES,
  RESTRICTION_CHANNELS,
  normalizeGeoRestriction,
  normalizeAudienceRestriction,
  type GeoRestriction,
  type AudienceRestriction,
  type RestrictionDevice,
  type RestrictionChannel,
} from "@/lib/types/experiment-restrictions";

interface DashboardContext {
  propertyId?: string;
  property?: any;
  currentDomain?: string;
  currentSite?: { domain?: string; propertyId?: string };
}
import { IconPlaceholder } from '@/components/ui/icon-placeholder'
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Add01Icon,
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  CalculatorIcon,
  CheckmarkBadge01Icon,
  ComputerIcon,
  Cursor01Icon,
  Delete01Icon,
  EarthIcon,
  Edit01Icon,
  File02Icon,
  Globe02Icon,
  InformationCircleIcon,
  BulbIcon,
  Link01Icon,
  Loading01Icon,
  Location01Icon,
  Refresh01Icon,
  Target01Icon,
  Tick01Icon,
  UserGroupIcon,
} from "@hugeicons/core-free-icons"

interface VariantInput {
  id: string;
  url: string;
  label: string;
  notes: string;
  isControl: boolean;
  screenshots?: string[];
}

// Test goals aligned with API
const testGoals = EXPERIMENT_GOALS;

const TOTAL_STEPS = 3;
const variantLabels = ["A", "B", "C", "D"];

// Country options for the geo-restriction picker, sorted by display name.
const COUNTRY_OPTIONS = Object.entries(countryCodes)
  .map(([code, name]) => ({ code, name }))
  .sort((a, b) => a.name.localeCompare(b.name));

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Summarize the audience/geo restrictions for a collapsed subtitle or a review row.
 * Empty when no restriction is active.
 */
function summarizeRestrictions(
  geo: GeoRestriction,
  audience: AudienceRestriction
): string {
  const parts: string[] = [];
  const devices = audience.devices || [];
  const channels = audience.channels || [];
  const countries = geo.countries || [];
  if (devices.length) parts.push(devices.map(titleCase).join(", "));
  if (channels.length) parts.push(channels.map(titleCase).join(", "));
  if (countries.length) {
    parts.push(
      countries.length <= 3
        ? countries.join(", ")
        : `${countries.slice(0, 3).join(", ")} +${countries.length - 3}`
    );
  }
  return parts.join(" · ");
}

function normalizeVariantUrl(raw: string, isExternalFunnel: boolean): string {
  const url = raw.trim();
  if (!url) return url;
  if (isExternalFunnel) {
    if (!url.startsWith("/") && !url.startsWith("http")) return `/${url}`;
    return url;
  }
  if (url.startsWith("http://") || url.startsWith("https://")) {
    try { return new URL(url).pathname; } catch { return url; }
  }
  if (!url.startsWith("/")) return `/${url}`;
  return url;
}

// Step names for URL routing. Assignment/targeting folded into the Variants
// step (it's a single default-heavy toggle — not worth its own screen).
const STEP_NAMES = [
  "setup",
  "variants",
  "review",
] as const;
type StepName = (typeof STEP_NAMES)[number];

export { getAllDraftSplitTests } from "@/components/experiments/splitTestDraft";

function CreateExperimentInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const plannerState = location.state as PlannerState | null;
  const params = useParams<{
    workspaceId?: string;
    propertySlugId?: string;
    magicId?: string;
    password?: string;
    domain?: string;
    draftId?: string;
    step?: string;
  }>();
  const ctx = useOutletContext<DashboardContext>();
  const workspaceContext = useWorkspaceSafe();
  const { token: authToken } = useAuth();
  const propertyId = ctx?.propertyId || "";
  const currentDomain =
    ctx?.currentDomain ||
    (ctx as any)?.currentSite?.domain ||
    ctx?.property?.dataSources?.[0]?.config?.domain ||
    "";

  // Deep-link / onboarding context (?goal=&page=&platform=)
  const urlGoalParam = searchParams.get("goal");
  // Heatmaps "Test this element" → ?page=<url>&element=<selector>
  const { page: urlPageParam, element: urlElementParam } = parseTestDeepLink(searchParams);
  const urlPlatformParam = searchParams.get("platform");

  // Check if we're in workspace aggregate mode (All Sites selected)
  const isWorkspaceAggregateMode = !!workspaceContext && !workspaceContext.selectedPropertyId && workspaceContext.enabledProperties.length > 1;
  const enabledProperties = workspaceContext?.enabledProperties || [];

  // Resolved domain: falls back to enabledProperties lookup when dashboardData is stale/empty
  const resolvedDomain = currentDomain ||
    (workspaceContext?.selectedPropertyId
      ? enabledProperties.find(p => p.propertyId === workspaceContext.selectedPropertyId)?.domain
      : undefined) ||
    "";

  // Get the base path for navigation (same pattern used throughout the app)
  const getBasePath = () => {
    if (params.magicId && params.password) {
      return `/s/${params.magicId}/${params.password}`;
    }
    if (params.workspaceId) {
      return `/workspace/${params.workspaceId}`;
    }
    if (params.propertySlugId) {
      return `/p/${params.propertySlugId}`;
    }
    if (params.domain) {
      return `/d/${params.domain}`;
    }
    return "";
  };

  // Load initial state from URL params and localStorage
  const urlDraftId = params.draftId;
  const urlStep = params.step as StepName | undefined;

  // Helper to get step index from step name
  const getStepIndex = (stepName?: string): number => {
    if (!stepName) return 0;
    const index = STEP_NAMES.indexOf(stepName as StepName);
    return index >= 0 ? index : 0;
  };

  // Helper to get step name from index
  const getStepName = (index: number): StepName => {
    return STEP_NAMES[index] || "setup";
  };

  // Load draft from localStorage
  const loadDraftFromStorage = (
    id: string
  ): Partial<DraftSplitTestState> | null => {
    try {
      const stored = localStorage.getItem(`${DRAFT_STORAGE_KEY}${id}`);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error(
        "[CreateExperiment] Failed to load draft from localStorage:",
        e
      );
    }
    return null;
  };

  // Initial state from localStorage if draftId is in URL
  const initialDraft = urlDraftId ? loadDraftFromStorage(urlDraftId) : null;

  // Property selection for workspace aggregate mode - MUST be declared early
  // When user is viewing "All Sites", they need to select which property to test
  const [selectedPropertyForTest, setSelectedPropertyForTest] = useState<string>(
    initialDraft?.selectedPropertyForTest || (isWorkspaceAggregateMode ? "" : propertyId)
  );

  // Wizard state - derive from URL or default to 0
  const [currentStep, setCurrentStep] = useState(() => {
    if (urlStep) return getStepIndex(urlStep);
    if (initialDraft?.currentStep !== undefined)
      return initialDraft.currentStep;
    return 0;
  });

  // Form state - initialize from draft if available
  const [experimentName, setExperimentName] = useState(
    initialDraft?.experimentName || ""
  );
  const [testGoal, setTestGoal] = useState(initialDraft?.testGoal || "");
  const [finishPageUrl, setFinishPageUrl] = useState(
    initialDraft?.finishPageUrl || ""
  );
  const [finishPageInput, setFinishPageInput] = useState("");
  const [finishPageOpen, setFinishPageOpen] = useState(false);
  const [isUserLevel, setIsUserLevel] = useState(
    initialDraft?.isUserLevel || false
  );
  const [testType, setTestType] = useState<"page" | "element">(
    initialDraft?.testType || "element"
  );
  const [experimentNotes, setExperimentNotes] = useState(
    initialDraft?.experimentNotes || ""
  );
  // Default auto-stop is 30 days if not specified in draft
  const [autoStopDurationDays, setAutoStopDurationDays] = useState<number | null>(
    initialDraft?.autoStopDurationDays ?? 30
  );
  const [autoStopSelectValue, setAutoStopSelectValue] = useState<string>(() => {
    const d = initialDraft?.autoStopDurationDays ?? 30;
    if (d === 7) return "7";
    if (d === 14) return "14";
    if (d === 30) return "30";
    if (!d) return "none";
    return "custom";
  });
  const [autoStopCustomInput, setAutoStopCustomInput] = useState<string>(() => {
    const d = initialDraft?.autoStopDurationDays ?? 30;
    if (!d || d === 7 || d === 14 || d === 30) return "";
    return String(d);
  });
  const [trafficDistribution, setTrafficDistribution] = useState<number[]>(() => {
    const draftType = initialDraft?.testType || "element";
    const count =
      draftType === "element"
        ? 2
        : Math.max(initialDraft?.variants?.length ?? 2, 2);
    return normalizeTrafficDistribution(
      initialDraft?.trafficDistribution || [50, 50],
      count
    );
  });
  const [geoRestriction, setGeoRestriction] = useState<{
    countries?: string[];
    enabled: boolean;
  }>(initialDraft?.geoRestriction || { enabled: false });
  const [audienceRestriction, setAudienceRestriction] = useState<AudienceRestriction>(
    initialDraft?.audienceRestriction || { enabled: false }
  );
  const [variants, setVariants] = useState<VariantInput[]>(
    initialDraft?.variants || [
      {
        id: "A",
        url: "",
        label: "Control (A)",
        notes: "",
        isControl: true,
        screenshots: [],
      },
      {
        id: "B",
        url: "",
        label: "Variant B",
        notes: "",
        isControl: false,
        screenshots: [],
      },
    ]
  );
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [restrictionsOpen, setRestrictionsOpen] = useState(false);
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [showPlanningModal, setShowPlanningModal] = useState(false);

  // Component test state
  const [componentPageUrl, setComponentPageUrl] = useState(
    initialDraft?.componentPageUrl || ""
  );
  const [draftId, setDraftId] = useState<string | null>(urlDraftId || null);

  // Track if experiment was created (to prevent auto-save after cleanup)
  const experimentCreatedRef = React.useRef(false);

  // Mutable ref so cleanup effects always read the latest values without stale closure
  const setupTrackingRef = useRef({ pid: "", token: "", step: 0 });
  useEffect(() => {
    setupTrackingRef.current = {
      pid: selectedPropertyForTest || propertyId,
      token: authToken || "",
      step: currentStep,
    };
  });

  // Track each step viewed
  useEffect(() => {
    const pid = selectedPropertyForTest || propertyId;
    if (!pid || !authToken) return;
    trackSplitTestSetup(pid, STEP_NAMES[currentStep], "viewed", authToken);
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track abandoned when wizard is closed without completing
  useEffect(() => {
    return () => {
      if (experimentCreatedRef.current) return;
      const { pid, token, step } = setupTrackingRef.current;
      if (!pid || !token) return;
      trackSplitTestSetup(pid, STEP_NAMES[step], "abandoned", token);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track whether user attempted to advance on the current step (for inline validation hints)
  const [stepValidationAttempted, setStepValidationAttempted] = useState(false);
  const [aiSuggested, setAiSuggested] = useState(false);
  const aiPrefillAppliedRef = useRef(false);

  // Pre-fill from AI suggestion (sessionStorage)
  useEffect(() => {
    const stored = sessionStorage.getItem('ai-suggested-test');
    if (!stored) return;
    try {
      const suggestion = JSON.parse(stored);
      if (suggestion.testName) setExperimentName(suggestion.testName);
      if (suggestion.hypothesis) setExperimentNotes(suggestion.hypothesis);
      if (suggestion.pageUrl) setComponentPageUrl(suggestion.pageUrl);
      if (suggestion.suggestedChange) {
        setTestGoal(suggestion.suggestedChange);
      }
      aiPrefillAppliedRef.current = true;
      setAiSuggested(true);
    } catch { /* ignore parse errors */ }
    sessionStorage.removeItem('ai-suggested-test');
  }, []);

  // Auto-generate experiment name from goal + domain whenever goal or testType changes (unless AI-suggested)
  useEffect(() => {
    if (aiSuggested) return;
    if (!testGoal) return;
    const goal = testGoals.find((g) => g.id === testGoal);
    if (!goal) return;
    const domain = resolvedDomain || currentDomain || "site";
    const baseDomain = domain.replace(/^https?:\/\//, "").replace(/\/$/, "").split("/")[0];
    // Sentence case, like every other name in the app ("Form submissions test · acme.com").
    const goalName = goal.name.charAt(0).toUpperCase() + goal.name.slice(1).toLowerCase();
    const type = testType === "element" ? "" : "page ";
    setExperimentName(`${goalName} ${type}test · ${baseDomain}`);
  }, [testGoal, testType, resolvedDomain, currentDomain, aiSuggested]);

  // Save draft to localStorage whenever state changes
  const saveDraftToStorage = useMemo(() => {
    return (id: string) => {
      const draft: DraftSplitTestState = {
        draftId: id,
        createdAt: initialDraft?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        propertyId,
        experimentName,
        testGoal,
        finishPageUrl,
        isUserLevel,
        testType,
        experimentNotes,
        trafficDistribution,
        geoRestriction,
        audienceRestriction,
        variants,
        componentPageUrl,
        currentStep,
        selectedPropertyForTest,
        autoStopDurationDays,
      };
      localStorage.setItem(`${DRAFT_STORAGE_KEY}${id}`, JSON.stringify(draft));
    };
  }, [
    propertyId,
    experimentName,
    testGoal,
    finishPageUrl,
    isUserLevel,
    testType,
    experimentNotes,
    trafficDistribution,
    geoRestriction,
    audienceRestriction,
    variants,
    componentPageUrl,
    currentStep,
    selectedPropertyForTest,
    autoStopDurationDays,
    initialDraft?.createdAt,
  ]);

  // Hooks that depend on state - placed AFTER state declarations
  const createMutation = useCreateExperiment({ propertyId });

  // Plan-limit awareness — deep-link protection: a user can land directly on
  // this wizard (URL, bookmark, backlog "New") after the active test limit
  // was reached elsewhere, so this checks on entry rather than only relying
  // on the list page's gate.
  const { openPricingModal, currentPlan, planName } = useSubscription();
  const { splitTests: splitTestsUsage, metadata: planUsageMetadata } = usePlanUsage({ propertyId });
  const invalidatePlanUsage = useInvalidatePlanUsage();
  const displayPlanName = getPlanDisplayName(currentPlan, planUsageMetadata?.planLabel || planName);
  const splitTestLimitCopy = buildPlanLimitCopy({
    noun: "experiments",
    limit: splitTestsUsage.limit,
    planName: displayPlanName,
    resolution: "Stop or archive a test, or upgrade to launch this one.",
  });
  
  // In workspace aggregate mode, use selectedPropertyForTest for fetching pages
  // Otherwise use the context propertyId
  const propertyIdForPages = isWorkspaceAggregateMode && selectedPropertyForTest 
    ? selectedPropertyForTest 
    : propertyId;
    
  const {
    data: pagesData,
    refetch: refetchPages,
    isLoading: isPagesLoading,
    error: pagesError,
  } = useExperimentPages({
    propertyId: propertyIdForPages,
  });
  const pages = pagesData?.pages || [];

  // Sorted, deduplicated URL list for comboboxes — always includes "/" for the homepage
  const sortedPageUrls = useMemo(() => {
    const urls = pages.map((p: any) => p.url || p.page).filter(Boolean) as string[];
    const unique = Array.from(new Set(urls)).sort((a, b) => a.localeCompare(b));
    if (!unique.includes("/")) unique.unshift("/");
    return unique;
  }, [pages]);

  // Onboarding → experiment bridge (goal + platform soft-cache)
  const [siteContextTick, setSiteContextTick] = useState(0);
  const siteContext = useMemo(
    () => resolveSiteContext(propertyIdForPages),
    // siteContextTick re-reads after we persist a live detection
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [propertyIdForPages, siteContextTick],
  );
  const onboardingGoal = siteContext?.goal ?? null;
  const cachedPlatform = siteContext?.platform ?? null;

  // Same detection cascade as add-site install (domain → Firecrawl → HTML scrape).
  const canDetectPlatform = Boolean(resolvedDomain);
  const {
    platform: liveDetectedPlatform,
    isDetecting: isDetectingPlatform,
    isManual: isManualPlatform,
    setPlatform: setManualPlatform,
  } = useDetectSitePlatform({
    domain: resolvedDomain,
    token: authToken || "",
    propertyId: propertyIdForPages || undefined,
    enabled: canDetectPlatform,
  });

  useEffect(() => {
    if (!propertyIdForPages || !canDetectPlatform) return;
    if (isManualPlatform) return;
    if (!liveDetectedPlatform || liveDetectedPlatform === "generic") return;
    // Don't overwrite a cached non-generic platform unless still detecting upgrade.
    if (cachedPlatform && cachedPlatform !== "generic") return;
    setSiteContextPlatform(propertyIdForPages, liveDetectedPlatform, resolvedDomain || undefined);
    setSiteContextTick((n) => n + 1);
  }, [
    propertyIdForPages,
    canDetectPlatform,
    isManualPlatform,
    liveDetectedPlatform,
    cachedPlatform,
    resolvedDomain,
  ]);

  const resolvedPlatform: PlatformId | null = useMemo(() => {
    const fromUrl = urlPlatformParam ? normalizePlatformId(urlPlatformParam) : null;
    if (fromUrl && fromUrl !== "generic") return fromUrl;
    if (isManualPlatform && liveDetectedPlatform) {
      return liveDetectedPlatform === "generic" ? null : liveDetectedPlatform;
    }
    if (cachedPlatform && cachedPlatform !== "generic") return cachedPlatform;
    if (liveDetectedPlatform && liveDetectedPlatform !== "generic") return liveDetectedPlatform;
    return null;
  }, [urlPlatformParam, isManualPlatform, cachedPlatform, liveDetectedPlatform]);

  const handlePickPlatform = useCallback(
    (platform: PlatformId) => {
      setManualPlatform(platform);
      if (propertyIdForPages) {
        setSiteContextPlatform(propertyIdForPages, platform, resolvedDomain || undefined);
        setSiteContextTick((n) => n + 1);
      }
    },
    [propertyIdForPages, resolvedDomain, setManualPlatform],
  );

  const suggestedGoals = useMemo(
    () => suggestedExperimentGoals(onboardingGoal, resolvedPlatform),
    [onboardingGoal, resolvedPlatform],
  );
  // Badge Suggested only on the single top-ranked goal — three badges at once
  // dilute the nudge to noise.
  const hasGoalSuggestions = Boolean(onboardingGoal || resolvedPlatform);
  const topSuggestedGoal: ExperimentGoalId | null =
    hasGoalSuggestions && suggestedGoals.length > 0 ? suggestedGoals[0] : null;
  const recommendedTestType = useMemo(
    () => preferredTestType(resolvedPlatform),
    [resolvedPlatform],
  );
  const platformTip = useMemo(
    () => getPlatformVariantTip(resolvedPlatform),
    [resolvedPlatform],
  );
  const platformLabel = getPlatformLabel(resolvedPlatform);
  const onboardingGoalLabel = getOnboardingGoalLabel(onboardingGoal);
  const setupHint = getExperimentSetupHint(onboardingGoal, resolvedPlatform);
  // Always surface the strip when we have a site — detecting / known / pick.
  const showContextStrip = Boolean(resolvedDomain || onboardingGoal || platformLabel);

  // Persist platform from deep-link once; seed measurement goal + page once.
  const contextSeededRef = useRef(false);
  useEffect(() => {
    if (contextSeededRef.current) return;
    if (!propertyIdForPages) return;
    contextSeededRef.current = true;

    if (urlPlatformParam) {
      const normalized = normalizePlatformId(urlPlatformParam);
      if (normalized) {
        setSiteContextPlatform(propertyIdForPages, normalized, resolvedDomain || undefined);
      }
    }

    // Prefer URL experiment goal, else map onboarding goal — never overwrite draft/AI.
    if (
      !initialDraft?.testGoal &&
      !aiPrefillAppliedRef.current &&
      !testGoal
    ) {
      const fromUrl =
        urlGoalParam && urlGoalParam in EXPERIMENT_GOAL_LABELS
          ? (urlGoalParam as ExperimentGoalId)
          : isOnboardingGoalId(urlGoalParam)
            ? defaultExperimentGoal(urlGoalParam)
            : null;
      const next =
        fromUrl ??
        (onboardingGoal || resolvedPlatform
          ? defaultExperimentGoal(onboardingGoal, resolvedPlatform)
          : null);
      if (next) setTestGoal(next);
    }

    if (
      !initialDraft?.componentPageUrl &&
      !aiPrefillAppliedRef.current &&
      !componentPageUrl &&
      urlPageParam
    ) {
      setComponentPageUrl(urlPageParam);
    }
  }, [propertyIdForPages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Soft-seed measurement goal once platform / use case is known (custom domains).
  // Test type stays user-controlled — we only badge “Recommended” from preferredTestType.
  // Platform detection is async (and sometimes lands after the first seed), so we
  // allow exactly one upgrade to the sharper platform-fit goal — but only while the
  // user hasn't overridden our auto value.
  const goalSoftSeedRef = useRef<{ seeded: boolean; autoValue: ExperimentGoalId | null }>({
    seeded: false,
    autoValue: null,
  });
  useEffect(() => {
    if (aiPrefillAppliedRef.current || initialDraft?.testGoal) return;
    if (!onboardingGoal && !resolvedPlatform) return;
    const fit = defaultExperimentGoal(onboardingGoal, resolvedPlatform);

    if (!goalSoftSeedRef.current.seeded) {
      if (testGoal) return; // draft / user already chose one
      goalSoftSeedRef.current = { seeded: true, autoValue: fit };
      setTestGoal(fit);
      return;
    }

    // Detection resolved later — re-seed to the platform-fit goal only if the
    // user hasn't touched our previous auto value.
    if (
      resolvedPlatform &&
      fit !== goalSoftSeedRef.current.autoValue &&
      testGoal === goalSoftSeedRef.current.autoValue
    ) {
      goalSoftSeedRef.current.autoValue = fit;
      setTestGoal(fit);
    }
  }, [onboardingGoal, resolvedPlatform, testGoal, initialDraft?.testGoal]);

  // Suggest a page from traffic once pages load (if still empty).
  const pageSuggestedRef = useRef(false);
  const [autoSuggestedPage, setAutoSuggestedPage] = useState<string | null>(null);
  useEffect(() => {
    if (pageSuggestedRef.current) return;
    if (initialDraft?.componentPageUrl || aiSuggested || componentPageUrl) {
      pageSuggestedRef.current = true;
      return;
    }
    if (urlPageParam) {
      pageSuggestedRef.current = true;
      return;
    }
    if (isPagesLoading) return;
    pageSuggestedRef.current = true;
    const suggested = suggestPageForExperiment(pages, onboardingGoal, resolvedDomain);
    if (suggested) {
      setComponentPageUrl(suggested);
      setAutoSuggestedPage(suggested);
    }
  }, [
    pages,
    isPagesLoading,
    onboardingGoal,
    resolvedDomain,
    componentPageUrl,
    aiSuggested,
    urlPageParam,
    initialDraft?.componentPageUrl,
  ]);

  const [platformTipOpen, setPlatformTipOpen] = useState(true);

  // Fetch analytics data to estimate daily visitors for test planning
  const { data: analyticsData } = useAnalyticsData();
  const estimatedDailyVisitors = useMemo(() => {
    if (!analyticsData?.visitors) return undefined;
    // Estimate daily visitors from the current period data
    // Default period is usually 30 days
    return Math.round(analyticsData.visitors / 30);
  }, [analyticsData?.visitors]);

  // Auto-save to localStorage when state changes (debounced via effect)
  useEffect(() => {
    // Don't save after experiment is created (draft was already cleaned up)
    if (draftId && !experimentCreatedRef.current) {
      saveDraftToStorage(draftId);
    }
  }, [draftId, saveDraftToStorage]);

  // Navigate to step URL
  const navigateToStep = (stepIndex: number, newDraftId?: string) => {
    const id = newDraftId || draftId;
    const stepName = getStepName(stepIndex);
    const basePath = getBasePath();
    if (id) {
      navigate(`${basePath}/split-testing/new/${id}/${stepName}${location.search}`, {
        replace: true,
      });
    }
  };

  // Component test mutations
  const { data: draftData } = useAbTestDraft(draftId);

  // Get selected goal details
  const selectedGoal = useMemo(
    () => testGoals.find((g) => g.id === testGoal),
    [testGoal]
  );

  // Validation
  const canAdvance = useMemo(() => {
    switch (currentStep) {
      case 0: { // Setup — need goal + (finishPageUrl if required); name is auto-generated
        const needsFinish = selectedGoal?.requiresFinishPage;
        const hasFinish = !needsFinish || finishPageUrl.trim().length > 0;
        return !!testGoal && hasFinish;
      }
      case 1: { // Variants
        if (testType === "element") return componentPageUrl.trim().length > 0 && !!draftId;
        const allHaveUrls = variants.every((v) => v.url.trim());
        const urls = variants.map((v) => v.url.trim()).filter(Boolean);
        return allHaveUrls && urls.length === new Set(urls).size;
      }
      case 2: return true; // Review
      default: return false;
    }
  }, [
    currentStep,
    testGoal,
    testType,
    experimentName,
    finishPageUrl,
    selectedGoal,
    variants,
    componentPageUrl,
    draftId,
  ]);

  // Validation error messages per step
  const getStepValidationMessage = useCallback((step: number): string | null => {
    switch (step) {
      case 0: {
        if (!testGoal) return "Please select a goal.";
        if (!testGoal) return "Please select a goal.";
        if (selectedGoal?.requiresFinishPage && !finishPageUrl.trim()) return "Please enter a destination page URL.";
        return null;
      }
      case 1: {
        if (testType === "element") {
          if (!componentPageUrl.trim()) return "Please enter a page URL.";
          if (!draftId) return "Draft not initialized.";
          return null;
        }
        if (!variants.every((v) => v.url.trim())) return "All variants must have a URL.";
        const urls = variants.map((v) => v.url.trim()).filter(Boolean);
        if (urls.length !== new Set(urls).size) return "Variant URLs must be unique.";
        return null;
      }
      default: return null;
    }
  }, [testGoal, testType, experimentName, selectedGoal, finishPageUrl, componentPageUrl, draftId, variants]);

  const handleNext = () => {
    if (canAdvance) {
      const pid = selectedPropertyForTest || propertyId;
      if (pid && authToken) trackSplitTestSetup(pid, STEP_NAMES[currentStep], "completed", authToken);
      setStepValidationAttempted(false);
      // Generate draftId on first next if not already set
      if (!draftId) {
        const newDraftId = generateTestId();
        setDraftId(newDraftId);
        navigateToStep(currentStep + 1, newDraftId);
      } else {
        navigateToStep(currentStep + 1);
      }
      setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS - 1));
    } else {
      setStepValidationAttempted(true);
      const msg = getStepValidationMessage(currentStep);
      if (msg) {
        toast.error(msg);
      }
    }
  };

  const handleBack = () => {
    setStepValidationAttempted(false);
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      navigateToStep(prevStep);
    } else {
      navigate(-1);
    }
  };


  const handleGoToStep = (step: number) => {
    if (step <= currentStep) {
      setCurrentStep(step);
      navigateToStep(step);
    }
  };

  const handleTestTypeChange = (next: "page" | "element") => {
    if (next === testType) return;
    setTestType(next);
    // Keep a custom split when switching types; resize to the active variant count
    // (element is always Control/Variant B = 2 slots).
    const count = next === "element" ? 2 : Math.max(variants.length, 2);
    setTrafficDistribution((prev) => normalizeTrafficDistribution(prev, count));
  };

  const addVariant = () => {
    if (variants.length < 4) {
      const nextLabel = variantLabels[variants.length];
      const newVariant = {
        id: nextLabel,
        url: "",
        label: `Variant ${nextLabel}`,
        notes: "",
        isControl: false,
        screenshots: [],
      };
      setVariants([...variants, newVariant]);
      setTrafficDistribution(equalTrafficDistribution(variants.length + 1));
    }
  };

  const removeVariant = (index: number) => {
    if (variants.length > 2 && !variants[index].isControl) {
      const newVariants = variants.filter((_, i) => i !== index);
      // Re-label variants
      setVariants(
        newVariants.map((v, i) => ({
          ...v,
          id: variantLabels[i],
          label: v.isControl ? "Control (A)" : `Variant ${variantLabels[i]}`,
        }))
      );
      setTrafficDistribution(
        normalizeTrafficDistribution(
          trafficDistribution.filter((_, i) => i !== index),
          newVariants.length
        )
      );
    }
  };

  const updateVariant = (
    index: number,
    field: keyof VariantInput,
    value: any
  ) => {
    setVariants(
      variants.map((v, i) => (i === index ? { ...v, [field]: value } : v))
    );
  };

  // Generate a unique test ID (UUID v4)
  const generateTestId = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  };

  // Launch component editor
  const launchComponentEditor = () => {
    if (!componentPageUrl.trim()) {
      toast.error("Please enter a page URL first");
      return;
    }

    if (!draftId) {
      toast.error("Draft ID not found. Please go back and start again.");
      return;
    }
    
    // In workspace aggregate mode, need a property selected to get the domain
    if (isWorkspaceAggregateMode && !selectedPropertyForTest) {
      toast.error("Please select a property first");
      return;
    }
    
    // Get the domain from selected property or current context
    let domainToUse = resolvedDomain;
    if (isWorkspaceAggregateMode && selectedPropertyForTest) {
      const selectedProp = enabledProperties.find(p => p.propertyId === selectedPropertyForTest);
      domainToUse = selectedProp?.domain || "";
    }
    
      if (!domainToUse) {
      toast.error("Could not determine domain. Please select a property.");
      return;
    }

    // Construct full page URL
    const fullPageUrl = componentPageUrl.startsWith("http")
      ? componentPageUrl
      : `https://${domainToUse}${
          componentPageUrl.startsWith("/") ? "" : "/"
        }${componentPageUrl}`;

    // Navigate to the internal editor page with draftId and pageUrl
    const basePath = getBasePath();
    // Start the editor on the linked element while the page is the linked one.
    const startElement =
      urlElementParam && componentPageUrl === urlPageParam ? urlElementParam : null;
    navigate(
      `${basePath}/experiments/editor/${draftId}?pageUrl=${encodeURIComponent(
        fullPageUrl
      )}${startElement ? `&element=${encodeURIComponent(startElement)}` : ""}`
    );
  };

  // Derive savedVariants from the API response (draftData fetched via useAbTestDraft above)
  const savedVariants = useMemo<Record<string, { instructions: any[] }> | null>(() => {
    if (draftData?.data?.variant && draftData?.data?.instructions?.length > 0) {
      return { [draftData.data.variant]: { instructions: draftData.data.instructions } };
    }
    return null;
  }, [draftData]);

  // Get number of changes in component editor
  const getChangesCount = () => {
    if (!savedVariants) return 0;
    return Object.values(savedVariants).reduce(
      (acc: number, v: any) => acc + (v.instructions?.length || 0),
      0
    );
  };

  const handleCreate = async () => {
    if (!splitTestsUsage.canAdd) {
      openPricingModal({ reason: 'split_test_limit' });
      return;
    }
    const controlPath =
      testType === "element" ? componentPageUrl : variants[0]?.url || ""
    const variantPath =
      testType === "element" ? componentPageUrl : variants[1]?.url || ""
    const toAbsolute = (path: string) => {
      if (!path) return ""
      if (/^https?:\/\//i.test(path)) return path
      const domain = resolvedDomain || currentDomain
      if (!domain) return path
      const base = domain.startsWith("http") ? domain : `https://${domain}`
      return `${base.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}`
    }
    if (
      !isQAReady({
        controlUrl: toAbsolute(controlPath),
        variantUrl: toAbsolute(variantPath),
        isElementTest: testType === "element",
        changesCount: getChangesCount(),
        goalSet: Boolean(testGoal),
        trafficValid: trafficDistribution.reduce((a, b) => a + b, 0) === 100,
      })
    ) {
      toast.error(
        testType === "element" && getChangesCount() === 0
          ? "Make at least one change in the editor before launching. Otherwise both versions are the same page."
          : "Fix the checks marked in red before launching.",
      )
      return
    }

    try {
      if (testType === "element") {
        // For element tests, create with draft ID and component page URL
        // Get instructions from savedVariants (loaded from localStorage)
        const variantBInstructions = savedVariants?.["B"]?.instructions || [];
        const normalizedPageUrl = componentPageUrl.startsWith("/") ? componentPageUrl : `/${componentPageUrl}`;

        await createMutation.mutateAsync({
          experimentName,
          variants: [
            {
              id: "A",
              url: normalizedPageUrl,
              label: "Control",
              notes: "",
              isControl: true,
            },
            {
              id: "B",
              url: normalizedPageUrl,
              label: "Variant B",
              notes: "Component changes",
              isControl: false,
              instructions: variantBInstructions,
            },
          ],
          testGoal,
          isUserLevel,
          geoRestriction: normalizeGeoRestriction(geoRestriction),
          audienceRestriction: normalizeAudienceRestriction(audienceRestriction),
          finishPageUrl: finishPageUrl || undefined,
          testType: "component",
          notes: experimentNotes || undefined,
          draftId: draftId || undefined,
          trafficDistribution,
          autoStopDurationDays: autoStopDurationDays ?? undefined,
        });
      } else {
        // For page tests, use configured variants
        await createMutation.mutateAsync({
          experimentName,
          variants: variants.map((v) => ({
            id: v.id,
            url: normalizeVariantUrl(v.url, testGoal === "external_page_funnel"),
            label: v.label,
            notes: v.notes,
            isControl: v.isControl,
            screenshots: v.screenshots || [],
          })),
          testGoal,
          isUserLevel,
          finishPageUrl: finishPageUrl || undefined,
          testType: "redirect",
          notes: experimentNotes || undefined,
          trafficDistribution: trafficDistribution,
          geoRestriction: normalizeGeoRestriction(geoRestriction),
          audienceRestriction: normalizeAudienceRestriction(audienceRestriction),
          autoStopDurationDays: autoStopDurationDays ?? undefined,
        });
      }

      // Track successful launch
      const pid = selectedPropertyForTest || propertyId;
      if (pid && authToken) trackSplitTestSetup(pid, "review", "completed", authToken);

      // Mark as created to prevent auto-save from re-creating the draft
      experimentCreatedRef.current = true;

      // Clean up localStorage after successful creation
      if (draftId) {
        // Remove draft split test state
        localStorage.removeItem(`${DRAFT_STORAGE_KEY}${draftId}`);
        // Remove component editor instructions
        localStorage.removeItem(`humblytics_draft_instructions_${draftId}`);
      }

      track('experiment_published', authToken, {
        name: experimentName,
        test_type: testType,
        goal: testGoal,
        variation_count: testType === 'element' ? 2 : variants.length,
      })
      logExperimentEvent(
        "experiment_launched",
        { propertyId: selectedPropertyForTest || propertyId },
        { experimentName, experimentType: testType, draftId },
      );
      toast.success("Experiment created successfully!", {
        description: `"${experimentName}" is now running.`,
      });
      navigate(-1);
    } catch (e: any) {
      reportExperimentFailure(
        "launch",
        e,
        { propertyId: selectedPropertyForTest || propertyId },
        { experimentName, experimentType: testType, draftId },
      );
      if (isPlanLimitApiError(e)) {
        invalidatePlanUsage();
        toast.error("You've reached a limit of your current plan", {
          description: "This is a plan limit, not an error. Upgrade to launch this experiment.",
          action: {
            label: "Upgrade",
            onClick: () => openPricingModal({ reason: "split_test_limit" }),
          },
        });
        return;
      }
      toast.error("Failed to create experiment", {
        description: getUserFacingApiError(e),
        action: {
          label: "Try again",
          onClick: () => {
            handleCreate();
          },
        },
      });
    }
  };

  // ── Page picker (first screen of a fresh test) ───────────────────────
  const [pagePicked, setPagePicked] = useState(
    () => Boolean(urlDraftId || urlPageParam || initialDraft || (urlStep && urlStep !== "setup")),
  );
  const showPagePicker = !pagePicked && currentStep === 0 && !isWorkspaceAggregateMode && !aiSuggested;
  const plannerPlan = useMemo(() => planForPicker(plannerState), [plannerState]);
  const planSummary = useMemo(() => planSummaryLine(plannerState), [plannerState]);
  // Same query (and cache key) as the Test Planner: page traffic, last 30 days.
  const { data: pickerTraffic, isLoading: isPickerTrafficLoading } = useQuery({
    queryKey: ["planner-page-traffic", propertyIdForPages],
    queryFn: () => getPagesBreakdown("day_PAST_30_D", propertyIdForPages!, authToken!, 50),
    // Also kept on after the pick: the plan reads the chosen page's traffic.
    enabled: Boolean(propertyIdForPages && authToken && !isWorkspaceAggregateMode),
    staleTime: 5 * 60 * 1000,
  });
  const pickerPages = useMemo(
    () => buildPickerPages(pickerTraffic?.data, plannerPlan),
    [pickerTraffic, plannerPlan],
  );
  // Planner traffic comes from the page being tested (30 days); site-wide is the fallback.
  const testedPageDaily = useMemo(() => {
    const rows: Array<{ page?: string; unique_visitors?: number }> = pickerTraffic?.data ?? [];
    const norm = (p: string) => (p.split(/[?#]/)[0].replace(/^https?:\/\/[^/]+/i, "").replace(/\/+$/, "") || "/");
    const target = componentPageUrl ? norm(componentPageUrl) : null;
    const row = target ? rows.find((r) => r.page && norm(r.page) === target) : undefined;
    return row?.unique_visitors ? Math.max(1, Math.round(row.unique_visitors / PAGE_TRAFFIC_DAYS)) : null;
  }, [pickerTraffic, componentPageUrl]);
  const [planDays, setPlanDays] = useState<number | null>(null);
  // The no-code flow needs a real site: an enabled property with a domain. With
  // no site selected, the layout's domain falls back to the workspace name, so
  // the editor would load https://<name>/ and never connect.
  const pickerPropertyId = selectedPropertyForTest || propertyId;
  const pickerSiteBase =
    toSiteBase(enabledProperties.find((p) => p.propertyId === pickerPropertyId)?.domain) ||
    (workspaceContext ? null : toSiteBase(resolvedDomain));
  const isResolvingSite = !!workspaceContext?.isLoading;
  const hasPickerSite = Boolean(
    pickerPropertyId &&
      pickerSiteBase &&
      (!workspaceContext || enabledProperties.some((p) => p.propertyId === pickerPropertyId)),
  );
  const cleanPickerDomain = (pickerSiteBase || "").replace(/^https?:\/\//, "").replace(/^www\./, "");
  // Paper "01 New test · Pick a page": the pick opens the editor, where the
  // change is made and the test launched (Launch tab). No setup screens.
  const handlePickPage = (path: string, element?: string | null) => {
    const id = generateDraftId();
    const pid = selectedPropertyForTest || propertyId;
    writeSplitTestDraft(
      newElementDraft({
        draftId: id,
        propertyId: pid,
        pagePath: path,
        testGoal: testGoal || defaultExperimentGoal(onboardingGoal, resolvedPlatform),
        experimentName: `${pageTitle(path)} test`,
        selectedPropertyForTest: selectedPropertyForTest || undefined,
      }),
    );
    if (pid && authToken) trackSplitTestSetup(pid, "pick_page", "completed", authToken);
    const search = new URLSearchParams(location.search);
    search.set("pageUrl", path);
    if (element) search.set("element", element);
    else search.delete("element");
    search.delete("page");
    navigate(`${getBasePath()}/experiments/editor/${id}?${search.toString()}`, { replace: true });
  };
  // Heatmaps "Test this element" (?page=&element=) lands in the editor on that element.
  const deepLinkOpenedRef = useRef(false);
  useEffect(() => {
    if (deepLinkOpenedRef.current || !urlPageParam || urlDraftId || initialDraft || aiSuggested || isWorkspaceAggregateMode || !hasPickerSite) return;
    deepLinkOpenedRef.current = true;
    handlePickPage(urlPageParam, urlElementParam);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPageParam, urlElementParam, hasPickerSite]);

  // "Split by URL": the existing page-level (redirect) test path.
  const handleSplitByUrl = () => {
    handleTestTypeChange("page");
    setPagePicked(true);
  };

  const progressPercent = ((currentStep + 1) / TOTAL_STEPS) * 100;

  // Step titles for breadcrumb
  const stepTitles = ["Setup", "Variants", "Review"];

  // Assignment control — folded out of its own step into a quiet disclosure on
  // Variants. Default (per-visit) is fine for most tests; sticky is the opt-in.
  const assignmentDisclosure = (
    <Collapsible
      open={assignmentOpen}
      onOpenChange={setAssignmentOpen}
      className="border border-border/30 rounded-lg overflow-hidden bg-card"
    >
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between p-4 hover:bg-muted/20 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted/40 border border-border/40">
              <HugeiconsIcon icon={UserGroupIcon} className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-left">
              <p className="text-xs font-medium">Visitor assignment</p>
              <p className="text-[11px] text-muted-foreground/70">
                {assignmentOpen
                  ? "How a visitor is bucketed into a variant"
                  : `Currently: ${isUserLevel ? "Sticky visitor" : "Per visit"}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-[11px] font-medium px-1.5 py-0 h-4">
              Optional
            </Badge>
            <HugeiconsIcon icon={ArrowDown01Icon} className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform",
                assignmentOpen && "rotate-180"
              )} />
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-3 pt-0 border-t border-border/50 bg-muted/10">
          <RadioGroup
            value={isUserLevel ? "userLevel" : "groupLevel"}
            onValueChange={(v) => setIsUserLevel(v === "userLevel")}
            className="grid sm:grid-cols-2 gap-2.5 pt-3"
          >
            <label className={cn(
              "relative flex items-start p-3 rounded-lg border cursor-pointer transition-colors",
              !isUserLevel
                ? "border-primary/50 ring-1 ring-primary/15 bg-card"
                : "border-border/30 hover:border-border/60 hover:bg-muted/10",
            )}>
              <div className="flex gap-2.5">
                <RadioGroupItem value="groupLevel" id="assign-group" className="mt-0.5" />
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-xs">Per visit</span>
                    <Badge variant="secondary" className="text-[11px] px-1.5 py-0 h-4">Default</Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Faster results. Best for high-traffic, short tests.
                  </p>
                </div>
              </div>
            </label>
            <label className={cn(
              "relative flex items-start p-3 rounded-lg border cursor-pointer transition-colors",
              isUserLevel
                ? "border-primary/50 ring-1 ring-primary/15 bg-card"
                : "border-border/30 hover:border-border/60 hover:bg-muted/10",
            )}>
              <div className="flex gap-2.5">
                <RadioGroupItem value="userLevel" id="assign-user" className="mt-0.5" />
                <div className="space-y-1">
                  <span className="font-medium text-xs">Sticky visitor</span>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Same variant across visits. Best for journeys, pricing, longer tests.
                  </p>
                </div>
              </div>
            </label>
          </RadioGroup>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );

  // Audience & location restrictions — quiet disclosure on Variants, off by
  // default. Include-list semantics: an empty axis means "all". `enabled` on each
  // object is derived from whether that axis has selections.
  const restrictionDevices = audienceRestriction.devices || [];
  const restrictionChannels = audienceRestriction.channels || [];
  const restrictionCountries = geoRestriction.countries || [];
  const restrictionSummary = summarizeRestrictions(
    geoRestriction,
    audienceRestriction
  );

  const toggleRestrictionDevice = (device: RestrictionDevice) => {
    const next = restrictionDevices.includes(device)
      ? restrictionDevices.filter((d) => d !== device)
      : [...restrictionDevices, device];
    setAudienceRestriction({
      ...audienceRestriction,
      devices: next,
      enabled: next.length > 0 || restrictionChannels.length > 0,
    });
  };

  const toggleRestrictionChannel = (channel: RestrictionChannel) => {
    const next = restrictionChannels.includes(channel)
      ? restrictionChannels.filter((c) => c !== channel)
      : [...restrictionChannels, channel];
    setAudienceRestriction({
      ...audienceRestriction,
      channels: next,
      enabled: next.length > 0 || restrictionDevices.length > 0,
    });
  };

  const toggleRestrictionCountry = (code: string) => {
    const next = restrictionCountries.includes(code)
      ? restrictionCountries.filter((c) => c !== code)
      : [...restrictionCountries, code];
    setGeoRestriction({
      ...geoRestriction,
      countries: next,
      enabled: next.length > 0,
    });
  };

  const toggleChipClass = (active: boolean) =>
    cn(
      "px-3 py-1.5 rounded-md border text-xs font-medium capitalize transition-colors",
      active
        ? "border-border/60 bg-muted text-foreground"
        : "border-border/30 text-muted-foreground hover:bg-muted/40"
    );

  const restrictionsDisclosure = (
    <Collapsible
      open={restrictionsOpen}
      onOpenChange={setRestrictionsOpen}
      className="border border-border/30 rounded-lg overflow-hidden bg-card"
    >
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between p-4 hover:bg-muted/20 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted/40 border border-border/40">
              <HugeiconsIcon icon={Location01Icon} className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-left">
              <p className="text-xs font-medium">Audience & location</p>
              <p className="text-[11px] text-muted-foreground/70">
                {restrictionSummary
                  ? `Currently: ${restrictionSummary}`
                  : "Currently: All visitors, all locations"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-[11px] font-medium px-1.5 py-0 h-4">
              Optional
            </Badge>
            <HugeiconsIcon icon={ArrowDown01Icon} className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform",
                restrictionsOpen && "rotate-180"
              )} />
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-4 pt-0 border-t border-border/50 bg-muted/10 space-y-4">
          {/* Device types */}
          <div className="space-y-2 pt-4">
            <div className="flex items-center gap-2">
              <HugeiconsIcon icon={ComputerIcon} className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-xs font-medium">Device types</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {RESTRICTION_DEVICES.map((device) => (
                <button
                  key={device}
                  type="button"
                  onClick={() => toggleRestrictionDevice(device)}
                  className={toggleChipClass(restrictionDevices.includes(device))}
                >
                  {device}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground/70">
              Leave all off to include every device.
            </p>
          </div>

          {/* Traffic sources */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <HugeiconsIcon icon={UserGroupIcon} className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-xs font-medium">Traffic sources</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {RESTRICTION_CHANNELS.map((channel) => (
                <button
                  key={channel}
                  type="button"
                  onClick={() => toggleRestrictionChannel(channel)}
                  className={toggleChipClass(restrictionChannels.includes(channel))}
                >
                  {channel}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground/70">
              Leave all off to include every traffic source.
            </p>
          </div>

          {/* Countries */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <HugeiconsIcon icon={EarthIcon} className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-xs font-medium">Countries</p>
            </div>
            {restrictionCountries.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {restrictionCountries.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => toggleRestrictionCountry(code)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border/60 bg-muted text-xs font-medium text-foreground hover:bg-muted/70 transition-colors"
                  >
                    <span>{countryCodes[code] || code}</span>
                    <HugeiconsIcon icon={Delete01Icon} className="h-3 w-3 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}
            <Popover open={countryPickerOpen} onOpenChange={setCountryPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs font-medium"
                >
                  <HugeiconsIcon icon={Add01Icon} className="h-3.5 w-3.5 mr-1.5" />
                  Add country
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[260px]" align="start">
                <Command>
                  <CommandInput placeholder="Search countries..." className="text-xs" />
                  <CommandList>
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup>
                      {COUNTRY_OPTIONS.map(({ code, name }) => {
                        const selected = restrictionCountries.includes(code);
                        return (
                          <CommandItem
                            key={code}
                            value={`${name} ${code}`}
                            onSelect={() => toggleRestrictionCountry(code)}
                            className="text-xs"
                          >
                            <div
                              className={cn(
                                "mr-2 flex h-3.5 w-3.5 items-center justify-center rounded-sm border",
                                selected
                                  ? "border-foreground/60 bg-foreground/10"
                                  : "border-border/60"
                              )}
                            >
                              {selected && (
                                <HugeiconsIcon icon={Tick01Icon} className="h-2.5 w-2.5 text-foreground" />
                              )}
                            </div>
                            <span className="flex-1">{name}</span>
                            <span className="text-muted-foreground/60 tabular-nums">{code}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-[11px] text-muted-foreground/70">
              Leave empty to include every location. Only visitors from the
              selected countries enter the test.
            </p>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );

  // Animation variants
  const contentVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" as const }
    },
    exit: { 
      opacity: 0, 
      y: -8,
      transition: { duration: 0.2 }
    }
  };

  const staggerContainer = {
    visible: {
      transition: { staggerChildren: 0.06, delayChildren: 0.1 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 8 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" as const }
    }
  };

  // ── Step 0a: pick a page ─────────────────────────────────────────────
  // Paper → Experiments "01 New test · Pick a page" / "01b · From planner".
  // Shown on a fresh start only: drafts, deep links (?page=) and AI
  // prefills already know the page.
  if (showPagePicker && !hasPickerSite && !isResolvingSite) {
    return (
      <div className="min-h-screen bg-background px-4 pb-16 sm:px-6">
        <ShellHeaderActions>
          <Button variant="outline" size="sm" className="h-8 px-3" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </ShellHeaderActions>
        <div className="mx-auto mt-16 max-w-[560px]">
          <EmptyState
            title="Pick a site to test"
            description="This workspace has no active site with a domain yet. Add a site or turn one on in Workspace settings, then start the test from it."
            action={
              <Button onClick={() => navigate(`${getBasePath()}/settings`)}>Open workspace settings</Button>
            }
          />
        </div>
      </div>
    );
  }

  if (showPagePicker) {
    return (
      <div className="min-h-screen bg-background px-4 pb-16 sm:px-6">
        <ShellHeaderActions>
          <Button variant="outline" size="sm" className="h-8 px-3" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </ShellHeaderActions>
        {!splitTestsUsage.canAdd && (
          <div className="mx-auto mt-8 max-w-[720px] sm:px-5">
            <PlanLimitBanner
              title={splitTestLimitCopy.title}
              description={splitTestLimitCopy.description}
              noun="experiments"
              planName={displayPlanName}
              isOwner={planUsageMetadata?.isOwner ?? true}
            />
          </div>
        )}
        <NewTestPagePicker
          pages={pickerPages}
          loading={isPickerTrafficLoading}
          domain={cleanPickerDomain}
          planSummary={planSummary}
          plan={plannerPlan}
          onPick={handlePickPage}
          onSplitByUrl={handleSplitByUrl}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Full-screen loading overlay when creating experiment */}
      {createMutation.isPending && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background">
          <div className="text-center max-w-sm mx-auto px-6">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h2 className="text-lg font-semibold text-foreground mb-2">
              Creating experiment
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Preparing your variant setup and saving. This may take a few seconds.
            </p>
            <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: "70%" }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sticky header — PageShell density */}
      <div className="sticky top-0 z-10 bg-background border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3.5">
          {/* Top row: back / step counter / cancel */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={handleBack}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
            >
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
              <span className="font-medium">
                {currentStep === 0 ? "Back to Experiments" : "Back"}
              </span>
            </button>

            <div className="hidden sm:flex items-center gap-2">
              <span className="text-[11px] font-medium text-muted-foreground tabular-nums">
                {currentStep + 1}/{TOTAL_STEPS}
              </span>
              <span className="h-3 w-px bg-border/60" aria-hidden />
              <span className="text-[11px] font-medium text-foreground">
                {stepTitles[currentStep]}
              </span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-muted-foreground hover:text-foreground h-8 px-3 text-xs font-medium"
            >
              Cancel
            </Button>
          </div>

          {/* Segmented progress track */}
          <div className="flex gap-1.5">
            {stepTitles.map((title, i) => {
              const isCompleted = i < currentStep;
              const isCurrent = i === currentStep;
              const isFuture = i > currentStep;

              return (
                <button
                  key={i}
                  onClick={() => handleGoToStep(i)}
                  disabled={isFuture}
                  title={title}
                  className={cn(
                    "relative flex-1 h-1.5 rounded-full transition-all duration-300 group",
                    isFuture ? "cursor-not-allowed" : "cursor-pointer"
                  )}
                >
                  {/* Track background */}
                  <div
                    className={cn(
                      "absolute inset-0 rounded-full transition-colors duration-300",
                      isCompleted && "bg-primary",
                      isCurrent && "bg-primary/30",
                      isFuture && "bg-border/40"
                    )}
                  />

                  {/* Animated fill for current segment */}
                  {isCurrent && (
                    <motion.div
                      className="absolute inset-y-0 left-0 rounded-full bg-primary"
                      initial={{ width: "0%" }}
                      animate={{ width: "50%" }}
                      transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
                    />
                  )}

                  {/* Hover tooltip-style label */}
                  {!isFuture && (
                    <span className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-medium text-foreground bg-popover border border-border/60 shadow-sm rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      {title}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Plan-limit banner — deep-link protection: a user can land directly
            on /split-testing/new (URL, bookmark, backlog "New") after hitting
            the active-test limit elsewhere. Non-dismissible since the Launch
            button below is also blocked. */}
        {!splitTestsUsage.canAdd && (
          <PlanLimitBanner
            className="mb-6"
            title={splitTestLimitCopy.title}
            description={splitTestLimitCopy.description}
            noun="experiments"
            planName={displayPlanName}
            isOwner={planUsageMetadata?.isOwner ?? true}
          />
        )}
        {/* Pre-filled from Test Planner banner */}
        {plannerState?.fromPlanner && (
          <Alert className="mb-6 border-border/40 bg-muted/20">
            <AlertDescription className="text-xs text-muted-foreground">
              Settings pre-filled from Test Planner
              {plannerState.estimatedDailyVisitors && (
                <span> &mdash; estimated {plannerState.estimatedDailyVisitors.toLocaleString()} daily visitors</span>
              )}
              {plannerState.estimatedDuration && (
                <span>, ~{plannerState.estimatedDuration} day duration</span>
              )}
            </AlertDescription>
          </Alert>
        )}
        <AnimatePresence mode="wait">
        {/* Step 0: Setup (Goal + Test Type + Name + Finish URL) */}
        {currentStep === 0 && (
          <motion.div
            key="setup"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={contentVariants}
            className="space-y-6"
          >
            <motion.div variants={staggerItem} className="space-y-3">
              <div>
                <h2 className="text-lg sm:text-xl font-medium tracking-tight text-foreground">New split test</h2>
                <p className="text-sm text-muted-foreground mt-1">Pick what to measure, then how to run the test.</p>
              </div>

              {showContextStrip && (
                <ExperimentPlatformStrip
                  domain={resolvedDomain}
                  platform={resolvedPlatform}
                  isDetecting={isDetectingPlatform}
                  useCaseLabel={onboardingGoalLabel}
                  hint={
                    setupHint ??
                    (hasGoalSuggestions && suggestedGoals.length > 0
                      ? `Suggested: ${suggestedGoals
                          .slice(0, 2)
                          .map((id) => EXPERIMENT_GOAL_LABELS[id])
                          .join(" · ")}`
                      : null)
                  }
                  onSelect={handlePickPlatform}
                />
              )}
            </motion.div>

            {/* Section 1: Goal */}
            <motion.div className="space-y-3" variants={staggerContainer} initial="hidden" animate="visible">
              <motion.div variants={staggerItem}>
                <p className="text-sm font-medium text-foreground">What do you want to optimize?</p>
                <p className="text-xs text-muted-foreground mt-0.5">Measurement goal for this experiment.</p>
              </motion.div>

              {/* Conversion Goals */}
              <motion.div variants={staggerItem}>
                <SectionEyebrow label="Conversion" meta="Track a completed action" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {testGoals
                    .filter((g) => g.type === "conversion")
                    .map((goal) => {
                      const isSelected = testGoal === goal.id;
                      const isSuggested = topSuggestedGoal === goal.id;
                      return (
                        <TooltipProvider key={goal.id} delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <motion.button
                                onClick={() => setTestGoal(goal.id)}
                                whileTap={{ scale: 0.98 }}
                                className={cn(
                                  "w-full h-full text-left p-3.5 rounded-lg border bg-card transition-colors duration-150 relative group",
                                  isSelected
                                    ? "border-foreground/80 bg-muted/40 ring-1 ring-foreground/10"
                                    : isSuggested
                                      ? "border-border/50 bg-muted/20 hover:border-border/70"
                                      : "border-border/30 hover:border-border/60 hover:bg-muted/10",
                                )}
                              >
                                {!isSelected && (
                                  <div className="absolute top-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <HugeiconsIcon icon={InformationCircleIcon} size={12} className="text-muted-foreground/50" />
                                  </div>
                                )}
                                <div className="flex items-center gap-3">
                                  <div className={cn(
                                    "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border",
                                    isSelected
                                      ? "bg-card border-foreground/20"
                                      : "bg-muted/40 border-border/40"
                                  )}>
                                    <IconPlaceholder hugeicons={goal.icon as any} className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <p className="font-medium text-sm text-foreground">{goal.name}</p>
                                      {isSuggested && !isSelected && (
                                        <Badge variant="secondary" className="text-[11px] px-1.5 py-0 h-4 shrink-0">Suggested</Badge>
                                      )}
                                      {goal.popular && !isSuggested && (
                                        <Badge variant="secondary" className="text-[11px] px-1.5 py-0 h-4 shrink-0">Popular</Badge>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{goal.description}</p>
                                  </div>
                                  {isSelected && (
                                    <div className="h-5 w-5 rounded-full bg-foreground flex items-center justify-center shrink-0">
                                      <HugeiconsIcon icon={Tick01Icon} size={12} className="text-background" />
                                    </div>
                                  )}
                                </div>
                              </motion.button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs p-3 bg-popover text-popover-foreground border-border shadow-sm">
                              <p className="text-xs leading-relaxed mb-2 text-popover-foreground">{goal.explanation}</p>
                              <div className="flex items-center gap-1.5 pt-2 border-t border-border/30">
                                <span className="text-[11px] font-medium text-muted-foreground">Outcome</span>
                                <span className="text-[11px] text-foreground/80">{goal.outcome}</span>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                </div>
              </motion.div>

              {/* Engagement Goals */}
              <motion.div variants={staggerItem}>
                <SectionEyebrow label="Engagement" meta="Measure attention on the page" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {testGoals
                    .filter((g) => g.type === "engagement")
                    .map((goal) => {
                      const isSelected = testGoal === goal.id;
                      return (
                        <TooltipProvider key={goal.id} delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <motion.button
                                onClick={() => setTestGoal(goal.id)}
                                whileTap={{ scale: 0.98 }}
                                className={cn(
                                  "w-full h-full text-left p-3.5 rounded-lg border bg-card transition-colors duration-150 relative group",
                                  isSelected
                                    ? "border-foreground/80 bg-muted/40 ring-1 ring-foreground/10"
                                    : "border-border/30 hover:border-border/60 hover:bg-muted/10",
                                )}
                              >
                                {!isSelected && (
                                  <div className="absolute top-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <HugeiconsIcon icon={InformationCircleIcon} size={12} className="text-muted-foreground/50" />
                                  </div>
                                )}
                                <div className="flex items-center gap-3">
                                  <div className={cn(
                                    "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border",
                                    isSelected
                                      ? "bg-card border-foreground/20"
                                      : "bg-muted/40 border-border/40"
                                  )}>
                                    <IconPlaceholder hugeicons={goal.icon as any} className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <p className="font-medium text-sm text-foreground">{goal.name}</p>
                                      {topSuggestedGoal === goal.id && !isSelected && (
                                        <Badge variant="secondary" className="text-[11px] px-1.5 py-0 h-4 shrink-0">Suggested</Badge>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{goal.description}</p>
                                  </div>
                                  {isSelected && (
                                    <div className="h-5 w-5 rounded-full bg-foreground flex items-center justify-center shrink-0">
                                      <HugeiconsIcon icon={Tick01Icon} size={12} className="text-background" />
                                    </div>
                                  )}
                                </div>
                              </motion.button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs p-3 bg-popover text-popover-foreground border-border shadow-sm">
                              <p className="text-xs leading-relaxed mb-2 text-popover-foreground">{goal.explanation}</p>
                              <div className="flex items-center gap-1.5 pt-2 border-t border-border/30">
                                <span className="text-[11px] font-medium text-muted-foreground">Outcome</span>
                                <span className="text-[11px] text-foreground/80">{goal.outcome}</span>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                </div>
              </motion.div>

              {stepValidationAttempted && !testGoal && (
                <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-destructive font-medium">
                  Please select a goal to continue.
                </motion.p>
              )}
            </motion.div>

            {/* Section 2: Test Type */}
            <motion.div className="space-y-3" variants={staggerContainer} initial="hidden" animate="visible">
              <motion.div variants={staggerItem}>
                <p className="text-sm font-medium text-foreground">Test type</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  How you’ll create variants
                  {platformLabel ? ` on ${platformLabel}` : ""}.
                </p>
              </motion.div>
              <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 items-stretch">
                <button
                  type="button"
                  onClick={() => handleTestTypeChange("element")}
                  className={cn(
                    "h-full text-left p-3.5 rounded-lg border bg-card transition-colors duration-150",
                    testType === "element"
                      ? "border-foreground/80 bg-muted/40 ring-1 ring-foreground/10"
                      : "border-border/30 hover:border-border/60 hover:bg-muted/10",
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border",
                      testType === "element"
                        ? "bg-card border-foreground/20"
                        : "bg-muted/40 border-border/40"
                    )}>
                      <HugeiconsIcon icon={Cursor01Icon} size={16} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-medium text-sm text-foreground">No-code element test</p>
                        {recommendedTestType === "element" && (
                          <Badge variant="secondary" className="text-[11px] px-1.5 py-0 h-4">Recommended</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">Modify headlines, buttons, images visually.</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <Badge variant="outline" className="text-[11px] font-medium px-2 py-0.5 h-auto border-border/40">No deploy needed</Badge>
                        <Badge variant="outline" className="text-[11px] font-medium px-2 py-0.5 h-auto border-border/40">Headlines</Badge>
                        <Badge variant="outline" className="text-[11px] font-medium px-2 py-0.5 h-auto border-border/40">CTA buttons</Badge>
                      </div>
                    </div>
                    {testType === "element" && (
                      <div className="h-5 w-5 rounded-full bg-foreground flex items-center justify-center shrink-0">
                        <HugeiconsIcon icon={Tick01Icon} size={12} className="text-background" />
                      </div>
                    )}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleTestTypeChange("page")}
                  className={cn(
                    "h-full text-left p-3.5 rounded-lg border bg-card transition-colors duration-150",
                    testType === "page"
                      ? "border-foreground/80 bg-muted/40 ring-1 ring-foreground/10"
                      : "border-border/30 hover:border-border/60 hover:bg-muted/10",
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border",
                      testType === "page"
                        ? "bg-card border-foreground/20"
                        : "bg-muted/40 border-border/40"
                    )}>
                      <HugeiconsIcon icon={Globe02Icon} size={16} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-medium text-sm text-foreground">Page-level test</p>
                        {recommendedTestType === "page" && (
                          <Badge variant="secondary" className="text-[11px] px-1.5 py-0 h-4">Recommended</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">Test different page URLs against each other.</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <Badge variant="outline" className="text-[11px] font-medium px-2 py-0.5 h-auto border-border/40">Both URLs live</Badge>
                        <Badge variant="outline" className="text-[11px] font-medium px-2 py-0.5 h-auto border-border/40">Landing pages</Badge>
                        <Badge variant="outline" className="text-[11px] font-medium px-2 py-0.5 h-auto border-border/40">Pricing pages</Badge>
                      </div>
                    </div>
                    {testType === "page" && (
                      <div className="h-5 w-5 rounded-full bg-foreground flex items-center justify-center shrink-0">
                        <HugeiconsIcon icon={Tick01Icon} size={12} className="text-background" />
                      </div>
                    )}
                  </div>
                </button>
              </motion.div>
              {resolvedPlatform && (
                <motion.p variants={staggerItem} className="text-[11px] text-muted-foreground">
                  {testType === "page"
                    ? `Page-level tests compare two live URLs. Both pages must be published on ${platformLabel} before launch.`
                    : `On ${platformLabel}: variant changes apply through your tracking script at runtime. No deploy or republish needed.`}
                </motion.p>
              )}
            </motion.div>

            {/* Inline plan: hypothesis + power (replaces separate TestPlanner modal as primary) */}
            <ExperimentPlanPanel
              hypothesis={experimentNotes}
              onHypothesisChange={setExperimentNotes}
              dailyVisitors={testedPageDaily ?? plannerState?.estimatedDailyVisitors ?? estimatedDailyVisitors}
              dailyVisitorsSource={
                testedPageDaily != null
                  ? `${componentPageUrl || "/"}, last 30 days`
                  : plannerState?.estimatedDailyVisitors
                    ? "from the test planner"
                    : "whole site, last 30 days"
              }
              defaultPlan={plannerPlan}
              onEstimateChange={setPlanDays}
              variantCount={testType === "element" ? 2 : variants.length}
              goalId={testGoal}
            />

            {/* Section 4: Destination URL (if required) */}
            {selectedGoal?.requiresFinishPage && (
              <motion.div className="space-y-2" variants={staggerItem}>
                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                  {testGoal === "external_page_funnel" ? "External destination URL" : "Destination page"}
                  <span className="text-destructive">*</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HugeiconsIcon icon={InformationCircleIcon} size={12} className="text-muted-foreground/60" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-xs">
                          {testGoal === "external_page_funnel"
                            ? "The external URL that visitors should click to count as a conversion."
                            : "The page that indicates a successful conversion (e.g., /thank-you)"}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </p>
                <Popover open={finishPageOpen} onOpenChange={setFinishPageOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between h-9 text-sm">
                      {finishPageUrl || "Select or enter destination URL"}
                      <HugeiconsIcon icon={ArrowRight01Icon} size={14} className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] border-border/50">
                    <Command>
                      <CommandInput
                        placeholder={testGoal === "external_page_funnel" ? "Enter external URL (e.g., https://example.com)" : "Search pages or enter a custom URL..."}
                        value={finishPageInput}
                        onValueChange={setFinishPageInput}
                        className="h-8 text-xs"
                      />
                      <CommandList>
                        <CommandEmpty>
                          <p className="text-xs text-muted-foreground p-2">Press Enter to use "{finishPageInput}" as a custom URL.</p>
                        </CommandEmpty>
                        <CommandGroup>
                          {finishPageInput && (
                            <CommandItem
                              value={finishPageInput}
                              onSelect={() => {
                                const url = finishPageInput.startsWith("/") || finishPageInput.includes(".") ? finishPageInput : `/${finishPageInput}`;
                                setFinishPageUrl(url);
                                setFinishPageOpen(false);
                              }}
                              className="text-xs py-2"
                            >
                              <HugeiconsIcon icon={Add01Icon} size={14} className="mr-2 h-3.5 w-3.5" />
                              Use: {finishPageInput}
                            </CommandItem>
                          )}
                          {testGoal !== "external_page_funnel" &&
                            pages.slice(0, 20).map((page: any) => (
                              <CommandItem
                                key={page.page}
                                value={page.page}
                                onSelect={() => {
                                  setFinishPageUrl(page.page);
                                  setFinishPageInput(page.page);
                                  setFinishPageOpen(false);
                                }}
                                className="text-xs py-2"
                              >
                                <HugeiconsIcon icon={CheckmarkBadge01Icon} className={cn("mr-2 h-3.5 w-3.5", finishPageUrl === page.page ? "opacity-100" : "opacity-0")} />
                                {page.page}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-[11px] text-muted-foreground/60">
                  {testGoal === "external_page_funnel" ? "Enter the external URL. Wildcards (*) are supported." : "Select the destination page. Wildcards (*) are supported."}
                </p>
                {destinationCountsLogins(finishPageUrl) && (
                  <div role="note" className="flex gap-2 rounded-lg border border-warning/30 bg-warning-tint px-3 py-2">
                    <HugeiconsIcon icon={InformationCircleIcon} size={14} className="mt-0.5 shrink-0 text-warning" />
                    <p className="text-xs leading-[18px] text-foreground">
                      Customers who log in visit this page too, so it would count returning users as conversions.
                      To measure new trials, use a sign-up confirmation page or the Revenue goal, which also reports trials started in Stripe.
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Step 1: Variants (or Component Editor for element tests) */}
        {currentStep === 1 && testType === "element" && (
          <motion.div
            key="variants-element"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={contentVariants}
            className="space-y-6"
          >
            {/* Header + platform / page guidance */}
            <motion.div variants={staggerItem} className="space-y-3">
              <div>
                <h2 className="text-xl font-medium tracking-tight">{testType === "element" ? "Make your change" : "Configure variants"}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {testType === "element"
                    ? `Open the editor on ${componentPageUrl || "/"} and change a headline, button or style for version B.`
                    : "Pick a page, then open the editor to change headlines, CTAs, or styles."}
                </p>
              </div>
              {(resolvedPlatform || autoSuggestedPage) && (
                <div className="rounded-lg border border-border/40 bg-muted/15 px-3.5 py-3 space-y-2">
                  {platformLabel && resolvedPlatform && (
                    <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                      <PlatformGlyph platform={resolvedPlatform} className="size-4" />
                      <span>
                        Editing on <span className="font-medium text-foreground">{platformLabel}</span>
                      </span>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <HugeiconsIcon
                      icon={InformationCircleIcon}
                      size={14}
                      className="mt-0.5 shrink-0 text-success dark:text-success"
                    />
                    <p className="text-[12px] text-muted-foreground">
                      <span className="font-medium text-foreground">No deploy needed</span>
                      {" "}— your tracking script applies these edits at runtime
                      {platformLabel ? ` on ${platformLabel}` : ""}. Save in the editor and launch.
                    </p>
                  </div>
                  {autoSuggestedPage && componentPageUrl === autoSuggestedPage && (
                    <p className="text-[11px] text-muted-foreground">
                      Suggested page:{" "}
                      <span className="font-mono font-medium text-foreground">{autoSuggestedPage}</span>
                      {" "}based on your use case & traffic.
                    </p>
                  )}
                </div>
              )}
            </motion.div>

            {/* Saved Instructions Preview — shown above launch card when changes exist */}
            {savedVariants && getChangesCount() > 0 && (
              <motion.div variants={staggerItem}>
                <Card className="border border-border/30 bg-card">
                <CardContent className="p-4">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2 text-foreground">
                    <HugeiconsIcon icon={Edit01Icon} size={16} className="text-muted-foreground" />
                    Saved Changes ({getChangesCount()})
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(savedVariants).map(
                      ([variantKey, variantData]: [string, any]) =>
                        variantData.instructions?.map(
                          (instruction: any, idx: number) => (
                            <div
                              key={`${variantKey}-${idx}`}
                              className="p-3 rounded-lg bg-muted/20 border border-border/30 text-xs"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Badge
                                  variant="outline"
                                  className="text-[11px] font-semibold px-1.5 py-0 h-4"
                                >
                                  {instruction.op === "text"
                                    ? "Text Change"
                                    : instruction.op === "css"
                                    ? "Style Change"
                                    : instruction.op === "visibility"
                                    ? "Visibility"
                                    : instruction.op === "image"
                                    ? "Image Change"
                                    : instruction.op === "attribute"
                                    ? "Attribute"
                                    : instruction.op}
                                </Badge>
                                <span
                                  className="text-muted-foreground/70 truncate max-w-[200px] text-[11px]"
                                  title={instruction.selector}
                                >
                                  {instruction.selector}
                                </span>
                              </div>
                              <div className="text-foreground text-[11px]">
                                {instruction.op === "text" && (
                                  <span>
                                    → "
                                    {(typeof instruction.value === "string"
                                      ? instruction.value
                                      : instruction.value?.newText
                                    )?.substring(0, 60)}
                                    {(typeof instruction.value === "string"
                                      ? instruction.value?.length
                                      : instruction.value?.newText?.length) > 60
                                      ? "..."
                                      : ""}
                                    "
                                  </span>
                                )}
                                {instruction.op === "css" && (
                                  <span>
                                    →{" "}
                                    {Object.entries(instruction.value || {})
                                      .map(([k, v]) => `${k}: ${v}`)
                                      .join(", ")}
                                  </span>
                                )}
                                {instruction.op === "visibility" && (
                                  <span>
                                    →{" "}
                                    {instruction.value?.visible ? "Visible" : "Hidden"}
                                  </span>
                                )}
                                {instruction.op === "image" && (
                                  <span>→ New image source</span>
                                )}
                                {instruction.op === "attribute" && (
                                  <span>
                                    → {instruction.value?.name}: "
                                    {instruction.value?.value}"
                                  </span>
                                )}
                              </div>
                            </div>
                          )
                        )
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2.5 w-full h-8 text-xs font-medium"
                    onClick={launchComponentEditor}
                  >
                    <HugeiconsIcon icon={Edit01Icon} size={12} className="h-3 w-3 mr-1.5" />
                    Edit changes
                  </Button>
                </CardContent>
              </Card>
              </motion.div>
            )}

            {/* Main Editor Card - Combines page selection + launch */}
            <motion.div variants={staggerItem}>
              <Card className="border border-border/30 bg-card">
                <CardHeader className="border-b border-border/40 pb-3">
                  <CardTitle className="text-sm font-medium text-foreground">Page & editor</CardTitle>
                  <CardDescription className="text-xs text-muted-foreground">
                    {componentPageUrl ? "The page you picked. Change it here if needed." : "Choose the page, then open the visual editor"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {/* Property Selection - Only shown in workspace aggregate mode */}
                  {isWorkspaceAggregateMode && (
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground">
                        Which property are you testing? <span className="text-destructive">*</span>
                      </Label>
                      <RadioGroup
                        value={selectedPropertyForTest}
                        onValueChange={setSelectedPropertyForTest}
                        className="space-y-2"
                      >
                        {enabledProperties.map((prop) => (
                          <div key={prop.propertyId} className="flex items-center space-x-2">
                            <RadioGroupItem value={prop.propertyId} id={`prop-${prop.propertyId}`} />
                            <Label
                              htmlFor={`prop-${prop.propertyId}`}
                              className="text-sm font-normal cursor-pointer flex-1"
                            >
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{prop.name}</span>
                                <span className="text-xs text-muted-foreground">({prop.domain})</span>
                              </div>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                      {!selectedPropertyForTest && (
                        <p className="text-xs text-muted-foreground">
                          Select the website/app you want to run this experiment on
                        </p>
                      )}
                    </div>
                  )}

                  {/* Page URL Selection */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-muted-foreground">
                        Page to test <span className="text-destructive">*</span>
                      </Label>
                      {(pagesError || (pages.length === 0 && !isPagesLoading)) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => refetchPages()}
                          className="gap-1.5 h-7 text-xs font-medium"
                        >
                          <HugeiconsIcon icon={Refresh01Icon} size={12} className="h-3 w-3" />
                          Retry
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Combobox
                        value={componentPageUrl}
                        onValueChange={(value) => setComponentPageUrl(value || "")}
                        items={sortedPageUrls}
                        autoHighlight
                      >
                        <ComboboxInput
                          placeholder={
                            isPagesLoading
                              ? "Loading pages..."
                              : pages.length > 0
                              ? "Search pages..."
                              : `https://${resolvedDomain}/landing`
                          }
                          showClear={!!componentPageUrl}
                          className="flex-1 w-full h-9 text-sm"
                          disabled={isPagesLoading}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              const typed = e.currentTarget.value.trim();
                              if (typed && !sortedPageUrls.includes(typed)) {
                                setComponentPageUrl(typed);
                              }
                            }
                          }}
                          onBlur={(e) => {
                            const typed = e.currentTarget.value.trim();
                            if (typed) setComponentPageUrl(typed);
                          }}
                        />
                        <ComboboxContent className="bg-background border-border/50">
                          <ComboboxList className="bg-background">
                            {isPagesLoading && (
                              <div className="p-3 space-y-2">
                                {[1, 2, 3, 4, 5].map((i) => (
                                  <Skeleton key={i} className="h-9 w-full" />
                                ))}
                                <p className="text-xs text-muted-foreground pt-2 text-center">
                                  Loading pages from your site...
                                </p>
                              </div>
                            )}
                            <ComboboxEmpty>
                              {pagesError
                                ? "Failed to load pages. Type a URL manually."
                                : "No pages found. Enter URL manually."}
                            </ComboboxEmpty>
                            {!isPagesLoading && (
                              <ComboboxCollection>
                                {(url: string) => {
                                  const segments = url.split('/').filter(Boolean)
                                  const lastSegment = segments.length > 0 ? segments[segments.length - 1] : '/'
                                  const dirPath = segments.length > 1 ? '/' + segments.slice(0, -1).join('/') + '/' : '/'
                                  return (
                                    <ComboboxItem key={url} value={url} className="text-sm py-2.5">
                                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                        <HugeiconsIcon icon={File02Icon} size={14} className="shrink-0 text-muted-foreground" />
                                        <div className="flex flex-col min-w-0">
                                          <span className="truncate font-medium">{url === '/' ? '/' : lastSegment}</span>
                                          {url !== '/' && segments.length > 1 && (
                                            <span className="truncate text-xs text-muted-foreground">{dirPath}</span>
                                          )}
                                        </div>
                                      </div>
                                    </ComboboxItem>
                                  )
                                }}
                              </ComboboxCollection>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                      {componentPageUrl && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline" size="icon" className="h-9 w-9" asChild>
                                <a
                                  href={
                                    componentPageUrl.startsWith("http")
                                      ? componentPageUrl
                                      : `https://${resolvedDomain}${componentPageUrl}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <HugeiconsIcon icon={Link01Icon} size={14} />
                                </a>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Open page in new tab</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground/60">
                      {isPagesLoading
                        ? "Loading available pages from your site..."
                        : pagesError
                        ? "Failed to load pages. Please enter URL manually."
                        : pages.length > 0
                        ? `Select from ${pages.length} available page${
                            pages.length !== 1 ? "s" : ""
                          } or type a custom URL`
                        : "No pages found via API. Enter the full URL of the page you want to test"}
                    </p>
                  </div>

                  {urlElementParam && componentPageUrl === urlPageParam && getChangesCount() === 0 && (
                    <p className="text-xs leading-4 text-muted-foreground">
                      Starts on the element from your heatmap:{" "}
                      <code className="break-all rounded-sm bg-muted px-1 py-0.5 font-mono text-[12px] text-foreground">
                        {urlElementParam}
                      </code>
                    </p>
                  )}

                  {/* Launch Button - Full width, prominent */}
                  <div className="flex gap-2">
                    <Button
                      onClick={launchComponentEditor}
                      disabled={!componentPageUrl.trim()}
                      className="flex-1 gap-2 h-10 text-sm font-medium"
                    >
                      <HugeiconsIcon icon={Link01Icon} size={16} />
                      {getChangesCount() > 0 ? "Reopen editor" : "Open editor"}
                    </Button>
                  </div>
                  {componentPageUrl.trim() && getChangesCount() === 0 && (
                    <div className="flex items-start gap-2 rounded-lg border border-warning/25 bg-warning/[0.06] px-3 py-2.5">
                      <HugeiconsIcon
                        icon={InformationCircleIcon}
                        size={14}
                        className="mt-0.5 shrink-0 text-warning dark:text-warning"
                      />
                      <p className="text-[11px] text-muted-foreground leading-relaxed">
                        Open the editor and make at least one change, or Variant B will be
                        identical to your control and the test won&apos;t measure anything.
                      </p>
                    </div>
                  )}
                </CardContent>

                {/* Card footer: editor capabilities + sample-size planner in one quiet row */}
                <CardFooter className="border-t border-border/40 justify-between gap-3 pt-3">
                  <p className="text-[11px] text-muted-foreground truncate">
                    Editor: text · styles · images · show/hide · reorder · AI suggestions
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPlanningModal(true)}
                    className="shrink-0 h-7 gap-1.5 px-2.5 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <HugeiconsIcon icon={CalculatorIcon} size={13} />
                    Sample size
                  </Button>
                </CardFooter>
              </Card>

              <TestPlanningModal
                open={showPlanningModal}
                onOpenChange={setShowPlanningModal}
                defaultValues={{
                  dailyVisitors: estimatedDailyVisitors,
                  variantCount: 2,
                  trafficDistribution,
                }}
              />
            </motion.div>

            <motion.div variants={staggerItem}>
              <TrafficDistributionPanel
                variants={[
                  { id: "A", label: "Control", isControl: true },
                  { id: "B", label: "Variant B", isControl: false },
                ]}
                distribution={trafficDistribution}
                onChange={setTrafficDistribution}
              />
            </motion.div>

            <motion.div variants={staggerItem}>{assignmentDisclosure}</motion.div>
            <motion.div variants={staggerItem}>{restrictionsDisclosure}</motion.div>
          </motion.div>
        )}

        {/* Step 1: Variants (for page-level tests) */}
        {currentStep === 1 && testType === "page" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-medium tracking-tight">
                  Configure variants ({variants.length}/4)
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Add page URLs for each variant. Traffic splits evenly by default.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchPages()}
                  className="gap-1.5 h-8 text-xs font-medium px-3"
                >
                  <HugeiconsIcon icon={Refresh01Icon} size={14} className="h-3.5 w-3.5" />
                  Refresh
                </Button>
                {variants.length < 4 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addVariant}
                    className="gap-1.5 h-8 text-xs font-medium px-3"
                  >
                    <HugeiconsIcon icon={Add01Icon} size={14} className="h-3.5 w-3.5" />
                    Add variant
                  </Button>
                )}
              </div>
            </div>

            {/* Page-level tests compare live URLs — publish/deploy guidance applies here */}
            {platformTip && (
              <div className="rounded-lg border border-border/40 bg-muted/15 px-3.5 py-3 space-y-2">
                {platformLabel && resolvedPlatform && (
                  <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                    <PlatformGlyph platform={resolvedPlatform} className="size-4" />
                    <span>
                      Testing pages on <span className="font-medium text-foreground">{platformLabel}</span>
                    </span>
                  </div>
                )}
                <Collapsible open={platformTipOpen} onOpenChange={setPlatformTipOpen}>
                  <CollapsibleTrigger asChild>
                    <button type="button" className="flex w-full items-start gap-2 text-left">
                      <HugeiconsIcon
                        icon={InformationCircleIcon}
                        size={14}
                        className="mt-0.5 shrink-0 text-success dark:text-success"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] font-medium text-foreground">{platformTip.headline}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {platformTipOpen ? "Hide setup steps" : "Show setup steps"}
                        </p>
                      </div>
                      <HugeiconsIcon
                        icon={ArrowDown01Icon}
                        size={12}
                        className={cn(
                          "mt-0.5 shrink-0 text-muted-foreground transition-transform",
                          platformTipOpen && "rotate-180",
                        )}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ol className="mt-2 ml-5 list-decimal space-y-1 text-[11px] text-muted-foreground">
                      {platformTip.steps.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}

            {/* Shared traffic split — same control as no-code element tests */}
            <TrafficDistributionPanel
              variants={variants.map((v) => ({
                id: v.id,
                label: v.label || (v.isControl ? "Control" : "Variant"),
                isControl: v.isControl,
              }))}
              distribution={trafficDistribution}
              onChange={setTrafficDistribution}
            />

            {/* Test Planning Guidance — quiet row, matches element-step footer */}
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border/30 px-3.5 py-2.5">
              <div className="flex items-center gap-2.5 min-w-0">
                <HugeiconsIcon icon={CalculatorIcon} size={15} className="text-muted-foreground shrink-0" />
                <p className="text-xs text-muted-foreground truncate">
                  <span className="font-medium text-foreground">Sample size</span>
                  {" "}· estimate how long this test needs to run
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPlanningModal(true)}
                className="shrink-0 h-8 text-xs px-3"
              >
                Calculator
              </Button>
            </div>

            {/* Test Planning Modal */}
            <TestPlanningModal
              open={showPlanningModal}
              onOpenChange={setShowPlanningModal}
              defaultValues={{
                dailyVisitors: estimatedDailyVisitors,
                variantCount: variants.length,
                trafficDistribution: trafficDistribution,
              }}
            />

            <div className="grid md:grid-cols-2 gap-4">
              {variants.map((variant, index) => (
                <Card
                  key={variant.id}
                  className={cn(
                    "relative border bg-card",
                    variant.isControl
                      ? "border-border/60"
                      : "border-border/30"
                  )}
                >
                  <CardHeader className="border-b border-border/40 pb-3 px-4 pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "size-1.5 rounded-full shrink-0",
                            variant.isControl ? "bg-muted-foreground/50" : "bg-success",
                          )}
                        />
                        <Badge
                          variant={variant.isControl ? "outline" : "secondary"}
                          className="text-[11px] font-semibold px-2 py-0.5 h-5"
                        >
                          {variant.label ||
                            (variant.isControl ? "Control" : "Variant")}
                        </Badge>
                        {variant.isControl && (
                          <span className="text-[11px] text-muted-foreground/70 font-medium">
                            (Original)
                          </span>
                        )}
                      </div>
                      {!variant.isControl && variants.length > 2 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeVariant(index)}
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        >
                          <HugeiconsIcon
                            icon={Delete01Icon}
                            size={14}
                          />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 px-4 pt-4 pb-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        Variant URL <span className="text-destructive">*</span>
                      </Label>
                      <Combobox
                        value={variant.url}
                        onValueChange={(value) => {
                          updateVariant(index, "url", normalizeVariantUrl(value || "", testGoal === "external_page_funnel"));
                        }}
                        items={sortedPageUrls}
                        autoHighlight
                      >
                        <ComboboxInput
                          placeholder={
                            pages.length > 0
                              ? "Search pages..."
                              : "/page-url or https://example.com"
                          }
                          showClear={!!variant.url}
                          className="h-9 text-sm"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              const typed = e.currentTarget.value.trim();
                              if (typed && !sortedPageUrls.includes(typed)) {
                                updateVariant(index, "url", normalizeVariantUrl(typed, testGoal === "external_page_funnel"));
                              }
                            }
                          }}
                          onBlur={(e) => {
                            const typed = e.currentTarget.value.trim();
                            if (typed) {
                              updateVariant(index, "url", normalizeVariantUrl(typed, testGoal === "external_page_funnel"));
                            }
                          }}
                        />
                        <ComboboxContent className="bg-background border-border/50">
                          <ComboboxList className="bg-background">
                            <ComboboxEmpty>No pages found. Enter URL manually.</ComboboxEmpty>
                            <ComboboxCollection>
                              {(url: string) => {
                                const segments = url.split('/').filter(Boolean)
                                const lastSegment = segments.length > 0 ? segments[segments.length - 1] : '/'
                                const dirPath = segments.length > 1 ? '/' + segments.slice(0, -1).join('/') + '/' : '/'
                                return (
                                  <ComboboxItem key={url} value={url} className="text-sm py-2.5">
                                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                      <HugeiconsIcon icon={File02Icon} size={14} className="shrink-0 text-muted-foreground" />
                                      <div className="flex flex-col min-w-0">
                                        <span className="truncate font-medium">{url === '/' ? '/' : lastSegment}</span>
                                        {url !== '/' && segments.length > 1 && (
                                          <span className="truncate text-xs text-muted-foreground">{dirPath}</span>
                                        )}
                                      </div>
                                    </div>
                                  </ComboboxItem>
                                )
                              }}
                            </ComboboxCollection>
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                      <p className="text-[11px] text-muted-foreground/60">
                        {variant.isControl
                          ? "This is the URL you should direct ALL traffic to initially."
                          : `We'll redirect ${trafficDistribution[index]}% of traffic to this variant.`}
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Notes (Optional)</Label>
                      <Textarea
                        placeholder={
                          variant.isControl
                            ? "Notes about the control variant..."
                            : "What makes this variant different?"
                        }
                        value={variant.notes}
                        onChange={(e) =>
                          updateVariant(index, "notes", e.target.value)
                        }
                        className="min-h-[70px] text-sm"
                      />
                    </div>

                    {/* Screenshots */}
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Screenshots (Optional)</Label>
                      <div className="space-y-1.5">
                        <Input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            const urls = files.map((file) =>
                              URL.createObjectURL(file)
                            );
                            const currentScreenshots =
                              variant.screenshots || [];
                            updateVariant(index, "screenshots", [
                              ...currentScreenshots,
                              ...urls,
                            ]);
                          }}
                          className="cursor-pointer h-9 text-xs"
                        />
                        {variant.screenshots &&
                          variant.screenshots.length > 0 && (
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              {variant.screenshots.map((screenshot, imgIdx) => (
                                <div key={imgIdx} className="relative group">
                                  <img
                                    src={screenshot}
                                    alt={`Screenshot ${imgIdx + 1}`}
                                    className="w-full h-24 object-cover rounded border"
                                  />
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon"
                                    className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => {
                                      const newScreenshots =
                                        variant.screenshots?.filter(
                                          (_, i) => i !== imgIdx
                                        ) || [];
                                      updateVariant(
                                        index,
                                        "screenshots",
                                        newScreenshots
                                      );
                                    }}
                                  >
                                    <HugeiconsIcon
                                      icon={Delete01Icon}
                                      size={12}
                                    />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Upload screenshots to visually document this variant.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {assignmentDisclosure}
            {restrictionsDisclosure}
          </div>
        )}

        {/* Step 2: Review */}
        {currentStep === 2 && (
          <motion.div
            key="review"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={contentVariants}
            className="space-y-6"
          >
            <motion.div
              className="space-y-4"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={staggerItem}>
                <h2 className="text-xl font-medium tracking-tight">Review &amp; launch</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {(selectedGoal || platformLabel || componentPageUrl) ? (
                    <>
                      Measuring{" "}
                      <span className="font-medium text-foreground">
                        {selectedGoal?.name?.toLowerCase() || "your goal"}
                      </span>
                      {platformLabel && (
                        <>
                          {" "}on{" "}
                          <span className="font-medium text-foreground">{platformLabel}</span>
                        </>
                      )}
                      {componentPageUrl && (
                        <>
                          {" "}· page{" "}
                          <span className="font-mono text-foreground/90">{componentPageUrl}</span>
                        </>
                      )}
                      . Check the details, then start.
                    </>
                  ) : (
                    "Check the details below, then start the experiment."
                  )}
                </p>
              </motion.div>

              {/* Editable name — auto-generated but renameable before launch */}
              <motion.div variants={staggerItem} className="space-y-1.5">
                <Label htmlFor="experiment-name" className="text-xs font-medium text-muted-foreground">
                  Experiment name
                </Label>
                <Input
                  id="experiment-name"
                  value={experimentName}
                  onChange={(e) => setExperimentName(e.target.value)}
                  placeholder="Name this experiment"
                  className="h-9 text-sm"
                />
              </motion.div>
            </motion.div>

            {/* QA / side-by-side preview + launch checklist */}
            <QAPreview
              controlUrl={
                testType === "element"
                  ? (() => {
                      const path = componentPageUrl || "/"
                      if (/^https?:\/\//i.test(path)) return path
                      const domain = resolvedDomain || currentDomain
                      if (!domain) return path
                      const base = domain.startsWith("http") ? domain : `https://${domain}`
                      return `${base.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}`
                    })()
                  : (() => {
                      const path = variants[0]?.url || ""
                      if (!path) return ""
                      if (/^https?:\/\//i.test(path)) return path
                      const domain = resolvedDomain || currentDomain
                      if (!domain) return path
                      const base = domain.startsWith("http") ? domain : `https://${domain}`
                      return `${base.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}`
                    })()
              }
              variantUrl={
                testType === "element"
                  ? (() => {
                      const path = componentPageUrl || "/"
                      if (/^https?:\/\//i.test(path)) return path
                      const domain = resolvedDomain || currentDomain
                      if (!domain) return path
                      const base = domain.startsWith("http") ? domain : `https://${domain}`
                      return `${base.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}`
                    })()
                  : (() => {
                      const path = variants[1]?.url || ""
                      if (!path) return ""
                      if (/^https?:\/\//i.test(path)) return path
                      const domain = resolvedDomain || currentDomain
                      if (!domain) return path
                      const base = domain.startsWith("http") ? domain : `https://${domain}`
                      return `${base.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}`
                    })()
              }
              isElementTest={testType === "element"}
              changesCount={getChangesCount()}
              sampleTargetRealistic={planDays == null || planDays <= 60}
              sampleDetail={planDays != null ? `About ${formatAbout(planDays).replace(/^about /, "")} to a result.` : undefined}
              goalSet={Boolean(testGoal)}
              trafficValid={
                trafficDistribution.reduce((a, b) => a + b, 0) === 100
              }
            />

            {/* Summary Cards */}
            <motion.div 
              className="grid md:grid-cols-2 gap-4"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {/* Experiment Info */}
              <motion.div variants={staggerItem}>
                <Card className="border border-border/30 bg-card h-full">
                  <CardHeader className="border-b border-border/40 pb-3 px-5 pt-5">
                    <CardTitle className="text-sm font-medium flex items-center gap-2.5 text-foreground">
                      <div className="w-8 h-8 rounded-lg bg-muted/40 border border-border/40 flex items-center justify-center">
                        <HugeiconsIcon icon={Target01Icon} size={16} className="h-4 w-4 text-muted-foreground" />
                      </div>
                      Experiment details
                    </CardTitle>
                  </CardHeader>
                <CardContent className="space-y-3 px-5 pt-4 pb-5">
                  <div className="flex justify-between items-start">
                    <span className="text-xs text-muted-foreground font-medium">Goal</span>
                    <div className="text-right">
                      <span className="text-sm font-medium">
                        {selectedGoal?.name}
                      </span>
                      {finishPageUrl && (
                        <p className="text-xs text-muted-foreground font-mono mt-1">
                          {finishPageUrl}
                        </p>
                      )}
                    </div>
                  </div>
                  <Separator className="bg-border/30" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground font-medium">
                      Test type
                    </span>
                    <Badge variant="secondary" className="text-xs font-medium px-2.5 py-0.5">
                      {testType === "page" ? "Page-level" : "No-code"}
                    </Badge>
                  </div>
                  {platformLabel && resolvedPlatform && (
                    <>
                      <Separator className="bg-border/30" />
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-xs text-muted-foreground font-medium">Platform</span>
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                          <PlatformGlyph platform={resolvedPlatform} size={14} />
                          {platformLabel}
                        </span>
                      </div>
                    </>
                  )}
                  <Separator className="bg-border/30" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground font-medium">Assignment</span>
                    <span className="text-sm font-medium">
                      {isUserLevel ? "Sticky visitor" : "Per visit"}
                    </span>
                  </div>
                </CardContent>
              </Card>
              </motion.div>

              {/* Variants */}
              <motion.div variants={staggerItem}>
              <Card className="border border-border/30 bg-card h-full">
                <CardHeader className="border-b border-border/40 pb-3 px-5 pt-5">
                  <CardTitle className="text-sm font-medium flex items-center gap-2.5 text-foreground">
                    <div className="w-8 h-8 rounded-lg bg-muted/40 border border-border/40 flex items-center justify-center">
                      <HugeiconsIcon icon={Globe02Icon} size={16} className="h-4 w-4 text-muted-foreground" />
                    </div>
                    {testType === "element"
                      ? "Test page"
                      : `Variants (${variants.length})`}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 px-5 pt-4 pb-5">
                  {testType === "element" ? (
                    // For element/component tests, show the single page URL with Control/Variant B
                    <>
                      <div className="flex items-center gap-3 p-3.5 rounded-lg bg-muted/10 border border-border/30">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold bg-foreground/[0.08] text-foreground border border-border/40">
                          A
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate text-foreground">
                            {componentPageUrl || "No URL"}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Control • {trafficDistribution[0] ?? 50}% traffic
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3.5 rounded-lg bg-muted/10 border border-border/30">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold bg-muted text-muted-foreground">
                          B
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate text-foreground">
                            {componentPageUrl || "No URL"} + {getChangesCount()}{" "}
                            change(s)
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Variant • {trafficDistribution[1] ?? 50}% traffic
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    // For page tests, show all configured variants
                    variants.map((v, idx) => (
                      <div
                        key={v.id}
                        className="flex items-center gap-3 p-3.5 rounded-lg bg-muted/10 border border-border/30"
                      >
                        <div
                          className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold",
                            v.isControl
                              ? "bg-foreground/[0.08] text-foreground border border-border/40"
                              : "bg-muted text-muted-foreground"
                          )}
                        >
                          {v.id}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate text-foreground">
                            {v.url || "No URL"}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {v.isControl ? "Control" : "Variant"} •{" "}
                            {trafficDistribution[idx]}% traffic
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
              </motion.div>

              {/* Targeting */}
              <motion.div variants={staggerItem}>
              <Card className="border border-border/30 bg-card h-full">
                <CardHeader className="border-b border-border/40 pb-3 px-5 pt-5">
                  <CardTitle className="text-sm font-medium flex items-center gap-2.5 text-foreground">
                    <div className="w-8 h-8 rounded-lg bg-muted/40 border border-border/40 flex items-center justify-center">
                      <HugeiconsIcon icon={UserGroupIcon} size={16} className="text-muted-foreground" />
                    </div>
                    Targeting
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 px-5 pt-4 pb-5">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground font-medium">
                      Assignment
                    </span>
                    <Badge variant="outline" className="text-xs font-medium px-2.5 py-0.5">
                      {isUserLevel ? "Sticky visitor" : "Per visit"}
                    </Badge>
                  </div>
                  <Separator className="bg-border/30" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground font-medium">
                      Traffic split
                    </span>
                    <span className="text-sm font-medium tabular-nums">
                      {trafficDistribution.map((p) => `${p}%`).join(" / ")}
                    </span>
                  </div>
                  <Separator className="bg-border/30" />
                  <div className="flex justify-between items-start gap-3">
                    <span className="text-xs text-muted-foreground font-medium shrink-0">
                      Devices
                    </span>
                    <span className="text-sm font-medium text-right">
                      {restrictionDevices.length
                        ? restrictionDevices.map(titleCase).join(", ")
                        : "All"}
                    </span>
                  </div>
                  <Separator className="bg-border/30" />
                  <div className="flex justify-between items-start gap-3">
                    <span className="text-xs text-muted-foreground font-medium shrink-0">
                      Traffic sources
                    </span>
                    <span className="text-sm font-medium text-right">
                      {restrictionChannels.length
                        ? restrictionChannels.map(titleCase).join(", ")
                        : "All"}
                    </span>
                  </div>
                  <Separator className="bg-border/30" />
                  <div className="flex justify-between items-start gap-3">
                    <span className="text-xs text-muted-foreground font-medium shrink-0">
                      Countries
                    </span>
                    <span className="text-sm font-medium text-right">
                      {restrictionCountries.length
                        ? restrictionCountries
                            .map((c) => countryCodes[c] || c)
                            .join(", ")
                        : "All"}
                    </span>
                  </div>
                </CardContent>
              </Card>
              </motion.div>

            </motion.div>

            {experimentNotes && (
              <motion.div variants={staggerItem}>
                <Card className="border border-dashed border-border/30">
                  <CardContent className="pt-4 px-5 pb-5">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center shrink-0">
                        <HugeiconsIcon
                          icon={BulbIcon}
                          size={16}
                          className="text-muted-foreground"
                        />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1.5">
                          Notes
                        </p>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">
                          {experimentNotes}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            <motion.div variants={staggerItem}>
              <Alert className="border border-border/40 bg-muted/20 py-4">
                <HugeiconsIcon icon={InformationCircleIcon} size={16} className="text-muted-foreground" />
                <AlertDescription className="text-sm text-muted-foreground">
                  Once started, traffic will be split between variants as
                  configured. You can pause or stop the experiment at any time
                  from the experiments list.
                </AlertDescription>
              </Alert>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>
      </div>

      {/* Sticky footer — primary Continue CTA */}
      <div className="sticky bottom-0 bg-background border-t border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={handleBack} 
            className="gap-2 h-9 text-sm font-medium px-3 text-muted-foreground hover:text-foreground"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} size={16} className="h-4 w-4" />
            {currentStep === 0 ? "Cancel" : "Back"}
          </Button>

          {currentStep < TOTAL_STEPS - 1 ? (
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={handleNext}
                disabled={!canAdvance}
                size="lg"
                className="gap-2 h-10 text-sm font-medium px-5"
              >
                {currentStep === TOTAL_STEPS - 2 ? "Review" : "Continue"}
                <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
              </Button>
            </motion.div>
          ) : (
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <CreateOrUpgradeButton
                atLimit={!splitTestsUsage.canAdd}
                onClick={handleCreate}
                disabled={createMutation.isPending || (testType === "element" && getChangesCount() === 0)}
                title={testType === "element" && getChangesCount() === 0 ? "Make at least one change in the editor first" : undefined}
                size="lg"
                className="gap-2 h-10 text-sm font-medium px-5"
                noun="experiments"
                limit={splitTestsUsage.limit}
                planName={displayPlanName}
              >
                {createMutation.isPending ? (
                  <>
                    <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Start experiment
                    <HugeiconsIcon icon={Tick01Icon} size={16} />
                  </>
                )}
              </CreateOrUpgradeButton>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CreateExperiment() {
  return (
    <FeatureGate feature="splitTesting">
      <CreateExperimentInner />
    </FeatureGate>
  )
}
